import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FmdGoodOutlined, KeyboardBackspace, LockOutlined, RefreshOutlined } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkIcon from "@mui/icons-material/Link";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useConfig } from "../../../contexts/ConfigContext";
import { useOptions } from "../../../contexts/OptionsContext";
import { transformRelationships } from "../../../services/mediaEditService";
import {
  ApiEndpoint,
  Entity,
  EntityItem,
  GeoJSONFeature,
  MediaItem,
  MediaItemDetailed,
  MediaResponse,
  RelatedEntityItem,
  RelationshipEditorState
} from "../../../types";
import { apiFetch, buildItemDetailUrl, REACT_APP_API_ROOT } from "../../../utilities/api";
import { fetchItemWithRelationships } from "../../../utilities/dataSourceAdapters";
import { getMediaType } from "../../../utilities/mediaUtils";
import { buildRelationshipForPayload, extractMediaItems } from "../../../utilities/relationshipUtils";
import { handleFormSubmissionError } from "../../../utilities/errorHandlers";
import { PreviewMap } from "../../common/PreviewMap";
import { useAuthSelector } from "../../../store/auth";
import { useMapActions } from "../../../store/map";
import { useLayerActions } from "../../../store/layer";
import { isCmTableEndpoint, getCmTableSlugFromEndpoint, getCmTableRowQueryKey } from "../../../utilities/cmTableEndpoint";
import { deleteV2MediaItem } from "../../../services/v2Media";
import {
  AnchorType,
  IAnchorRelationshipsPatchPayload,
  createCmTableMediaRelationship,
  createSpatialDatasetMediaRelationship,
  deleteCmTableMediaRelationship,
  normaliseRelations,
  patchAnchorRelationships,
  setCmTablePrimaryImage,
  setSpatialDatasetPrimaryImage,
  getCmTableRow
} from "../../../services/cmTables";
import CmTableDrawMap from "../../map/CmTableDrawMap";
import useAuth from "../../../hooks/useAuth";
import RelationshipEditDialog from "../../relationships/RelationshipEditDialog";
import CmTableRowDataFields from "./CmTableRowDataFields";
import { resolveIconUrl } from "./CmTableRowDataFields";
import CmFormCaptureFields from "../schema-fields/CmFormCaptureFields";
import { useResolvedRowForm } from "../schema-fields/useResolvedRowForm";
import { buildCaptureValues, CaptureValues, splitCaptureValues } from "../../../utilities/formBuilder/captureKeys";
import {
  buildRecordDescription,
  buildRecordTitle,
  describeDescriptionSource,
  describeTitleSource,
  resolveDescriptionSource,
  resolveNameSource,
  shouldAdoptDerivedTitle
} from "../../../utilities/formBuilder/recordTitle";
import { isIconStyled, getRowStyleValue, resolveStyleOutputForValue, getStyleFieldKey } from "../../../utilities/cmTableStyle";
import { MediaCarousel, MediaCarouselProvider } from "../../common/media-carousel/MediaCarousel";
import GeometryInfo from "./GeometryInfo";
import RelationshipChips from "./RelationshipChips";
import RestrictionsPickerDialog from "./RestrictionsPickerDialog";
import { CMChip } from "../../common/CMChip";
import FormActionButton from "./FormActionButton";
import {
  extractGeometryFromItem,
  getGeometryType,
  getGeometryTypeMismatchError,
  getGeometryValidationHelperText,
  getRelationshipMediaThumbnailUrl,
  hasRelationships,
  sortMediaByPrimaryImage
} from "../../../utilities/entityFormUtils";
import { getEntityCategory, isAFile } from "../../../utilities/common";
import UploadProgressBars from "../../editor/UploadProgressBars";
import { useSelectionActions, useSelectionSelector } from "../../../store/selection";
import { usePopupActions } from "../../../store/popupStore";
import { useMediaStore } from "../../../store/media";
import { useQueryCacheInvalidator } from "../../../hooks/useQueryCacheInvalidator";
import { MediaItemWithRetry } from "./MediaItemWithRetry";
import { useS3Upload } from "../../../services/awsUploadService";
import DeleteDialog from "../../alerts-dialog/DeleteDialog";
import MediaUnlinkDialog from "../../alerts-dialog/MediaUnlinkDialog";
import { useDeleteEntityMediaMutation, useDeleteEntityMutation, useEntityMediaUnlinkMutation } from "../../../api/entity";
import { isFileSizeValid, prepareUploadFileDetail } from "../../../utilities/fileUtils";
import { extractExifFromFile } from "../../../utilities/exifUtils";
import { generateUUID } from "../../../utilities/uuid";
import { normaliseGeometryInput, stripZDimensions } from "../../../utilities/geoUtils";
import { resolveMapView } from "../../../utilities/mapView";

type EntityFormProps = {
  open: boolean;
  onClose: () => void;
  endpoint: ApiEndpoint | null;
  mode?: "create" | "edit";
  existingItem?: MediaItem | EntityItem;
  isLoading?: boolean;
  onCancel?: (item?: MediaItem | EntityItem) => void;
  onItemCreated?: (newItem: any) => void;
  onItemUpdated?: (updatedItem: any) => void;
  onItemDeleted?: (deletedItemId: number | string) => void;
};

interface FormData {
  name: string;
  description: string;
  geometry?: string; // GeoJSON as string for editing
  coordinates?: string;
}

interface MapData {
  areas: GeoJSONFeature[];
  points: GeoJSONFeature[];
}

/**
 * Keeps one derived row column (the name or the description) in step with the
 * fields it is built from.
 *
 * A row's stored value and the one its picked fields produce can legitimately
 * differ — the row may predate the current picks, or the backend may format the
 * concatenation slightly differently — so opening a record must not rewrite it.
 * The baseline captured on the first pass is what makes that distinction; it
 * restarts whenever `resetToken` changes (a different record, form or dialog open).
 */
function useDerivedRowColumn({
  enabled,
  derived,
  current,
  mode,
  resetToken,
  apply
}: {
  enabled: boolean;
  derived: string;
  current: string;
  mode: "create" | "edit";
  resetToken: string;
  apply: (value: string) => void;
}) {
  const baselineRef = useRef<string | null>(null);

  // Declared first so it runs BEFORE the sync effect on the commit where the
  // record changes — a freshly loaded row is never rewritten by the previous row's
  // baseline.
  useEffect(() => {
    baselineRef.current = null;
  }, [resetToken]);

  useEffect(() => {
    if (!enabled) return;
    const adopt = shouldAdoptDerivedTitle({ baseline: baselineRef.current, derived, currentName: current, mode });
    baselineRef.current = derived;
    if (adopt) apply(derived);
    // `current` is read to decide adoption, not to trigger the effect — including it
    // would re-run this the moment `apply` writes, looping.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, derived, mode, apply]);
}

export default function EntityForm({
  open,
  onClose,
  endpoint,
  mode = "create",
  isLoading = false,
  existingItem,
  onItemCreated,
  onItemUpdated,
  onItemDeleted,
  onCancel
}: EntityFormProps) {
  // PERFORMANCE MONITORING: Track what causes excessive re-renders
  const renderCountRef = useRef(0);
  const lastPropsRef = useRef<any>({ open, mode, existingItem });

  useEffect(() => {
    renderCountRef.current++;
    const currentProps = { open, mode, existingItem };
    const prevProps = lastPropsRef.current;

    const changedProps = [];
    if (prevProps.open !== open) changedProps.push("open");
    if (prevProps.mode !== mode) changedProps.push("mode");
    if (prevProps.existingItem !== existingItem) changedProps.push("existingItem");

    if (changedProps.length > 0) {
      console.log(`🔍 [EntityForm] PERF: Render #${renderCountRef.current}, props changed:`, {
        renderCount: renderCountRef.current,
        changedProps: changedProps,
        open: open,
        mode: mode,
        existingItemId: existingItem?.id,
        existingItemIdentity: existingItem === prevProps.existingItem ? "SAME" : "DIFFERENT"
      });
    }

    lastPropsRef.current = currentProps;
  });

  const { enqueueSnackbar } = useSnackbar();
  const config = useConfig();
  const { refreshLayer } = useLayerActions();
  const { options, removeOptionItem, upsertOptionItem, refreshOptions } = useOptions();
  const { user: profile } = useAuthSelector();
  const { canDelete, canUpdate, canCreate, canManageRestrictions } = useAuth();

  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: "",
    description: "",
    geometry: ""
  }); // Parsed GeoJSON for CmTableDrawMap — memoised so a stable object reference is
  // passed as the externalGeometry prop, preventing spurious Terra Draw clear/re-seed
  // cycles on every parent re-render.
  // Accepts Geometry, Feature, or FeatureCollection (expandToComponents handles all three).
  const parsedExternalGeometry = useMemo<GeoJSON.Geometry | GeoJSON.Feature | GeoJSON.FeatureCollection | null>(() => {
    if (!formData.geometry?.trim()) return null;
    try {
      return normaliseGeometryInput(JSON.parse(formData.geometry)) as GeoJSON.Geometry | GeoJSON.Feature | GeoJSON.FeatureCollection;
    } catch {
      return null;
    }
  }, [formData.geometry]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // CM-table row_data (schema-driven custom fields, e.g. `type` that drives icon/color).
  // Kept separate from name/description/geometry and merged into the create/edit payload.
  const [rowData, setRowData] = useState<Record<string, any>>({});

  // Capture validation errors reported by CmFormCaptureFields (keyed by capture key),
  // and a flag set on a blocked save so the schema fields reveal inline errors.
  const rowDataErrorsRef = useRef<Record<string, string>>({});
  const [showCaptureErrors, setShowCaptureErrors] = useState(false);

  // The scrollable form column — a blocked save scrolls the first error into view here.
  const [formColumnEl, setFormColumnEl] = useState<HTMLDivElement | null>(null);

  // The row's own per-row `map_display_config` (icon for points, colour for
  // polygon/line). A first-class sibling of `row_data` — NOT stored inside it —
  // sent with the create/update payload. Empty ⇒ the row uses the table default.
  const [mapDisplayConfig, setMapDisplayConfig] = useState<Record<string, any>>({});

  // Local state to track current item data (for proper relationship display)
  const [currentItem, setCurrentItem] = useState<MediaItem | EntityItem | null>(null);

  // ── Form-driven capture ────────────────────────────────────────────────────
  //
  // When the CM table is bound to a CmForm, that form is the record editor: its
  // fields render inline, in author order, and the row's own `name` and
  // `description` columns are DERIVED from the fields the author picked for them
  // (Form Settings → Record Title / Record Description). So neither is typed here:
  // a Name box would duplicate the title fields, and a Description box would offer
  // a second, competing description. The generic inputs remain for simple CM
  // tables that have no form at all.
  const resolvedForm = useResolvedRowForm({
    tableSlug: isCmTableEndpoint(endpoint) ? getCmTableSlugFromEndpoint(endpoint) || endpoint?.tableName || null : null,
    rowData
  });
  const activeSchema = resolvedForm.activeForm?.schema ?? null;
  const nameSource = useMemo(() => resolveNameSource(activeSchema), [activeSchema]);
  const descriptionSource = useMemo(() => resolveDescriptionSource(activeSchema), [activeSchema]);
  /** A form resolved for this row, so the form owns both derived columns. */
  const formResolved = resolvedForm.activeForm != null;
  /** The form names its own records — EntityForm's Name input would be a duplicate. */
  const formSuppliesName = nameSource.kind !== "manual";
  /** Each column is concatenated from picked fields rather than typed directly. */
  const titleIsDerived = nameSource.kind === "fields";
  const descriptionIsDerived = descriptionSource.kind === "fields";
  /**
   * The config says this table is form-bound but its schema hasn't arrived yet.
   * Hold the generic inputs back for that moment so a form-driven record doesn't
   * flash a Name box it is about to replace. If the lookup fails outright the
   * query settles, this goes false, and the generic inputs return as the fallback.
   */
  const awaitingFormSchema = Boolean(endpoint?.form?.uuid) && resolvedForm.isLoading;
  /**
   * The generic Description box is for form-less CM tables only. Once a form is in
   * play the description is whatever its picked fields build — and a form that
   * picks none simply has no description, rather than a spare text area above its
   * fields asking for one.
   */
  const showGenericDescription = !formResolved && !awaitingFormSchema;

  // The flat capture map the form fields read/write: `row_data` plus the reserved
  // `$name` / `$description` slots that map onto the row's own columns.
  const captureValues = useMemo<CaptureValues>(
    () => buildCaptureValues(rowData, formData.name, formData.description),
    [rowData, formData.name, formData.description]
  );

  const handleCaptureChange = useCallback((next: CaptureValues) => {
    const { name, description, rowData: nextRowData } = splitCaptureValues(next);
    setRowData(nextRowData);
    setFormData((prev) => ({
      ...prev,
      ...(name === undefined ? {} : { name }),
      ...(description === undefined ? {} : { description })
    }));
  }, []);

  const handleCaptureValidityChange = useCallback((errors: Record<string, string>) => {
    rowDataErrorsRef.current = errors;
  }, []);

  // The form's name, shown above its fields only when it adds something the dialog
  // title doesn't already say (a table and its form usually share a name).
  const formHeading = useMemo(() => {
    const formName = resolvedForm.activeForm?.name?.trim();
    if (!formName) return null;
    return formName.toLowerCase() === (endpoint?.name ?? "").trim().toLowerCase() ? null : formName;
  }, [resolvedForm.activeForm?.name, endpoint?.name]);

  // Bring the first unfilled form field into view after a blocked save. The error
  // markers only exist once the fields have re-rendered with errors revealed, so
  // this waits a frame.
  const scrollToFirstCaptureError = useCallback(() => {
    requestAnimationFrame(() => {
      const target = formColumnEl?.querySelector('[data-capture-error="true"]');
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [formColumnEl]);

  // A new record, a different record, or a different schema restarts both
  // derived-column baselines.
  const derivedResetToken = `${currentItem?.id ?? "new"}|${resolvedForm.activeForm?.uuid ?? ""}|${open}`;

  // Keep the derived title in step with the fields it is built from. Edit mode only
  // takes over once one of those fields actually changes, so simply opening a record
  // can't overwrite the name the backend already stored.
  useDerivedRowColumn({
    enabled: titleIsDerived,
    derived: useMemo(() => buildRecordTitle(activeSchema, captureValues), [activeSchema, captureValues]),
    current: formData.name,
    mode,
    resetToken: derivedResetToken,
    apply: useCallback((value: string) => setFormData((prev) => (prev.name === value ? prev : { ...prev, name: value })), [])
  });

  // The description follows exactly the same rule from its own picked fields.
  useDerivedRowColumn({
    enabled: descriptionIsDerived,
    derived: useMemo(() => buildRecordDescription(activeSchema, captureValues), [activeSchema, captureValues]),
    current: formData.description,
    mode,
    resetToken: derivedResetToken,
    apply: useCallback((value: string) => setFormData((prev) => (prev.description === value ? prev : { ...prev, description: value })), [])
  });

  // Map state
  const [selectedCoordinates, setSelectedCoordinates] = useState<[number, number] | null>(null);
  const [selectedPolygon, setSelectedPolygon] = useState<GeoJSON.Polygon | null>(null);
  const [mapData, setMapData] = useState<MapData>({
    areas: [],
    points: []
  });
  const [mapSearchTerm, setMapSearchTerm] = useState("");
  const [showEditRelationships, setShowEditRelationships] = useState(false);
  // "Restrictions" multi-select (a plain `restrictions: number[]` field on the row/media
  // item, PATCHed directly — not part of the relationships system).
  const [selectedRestrictionIds, setSelectedRestrictionIds] = useState<number[]>([]);
  const originalRestrictionIdsRef = useRef<number[]>([]);
  const [showRestrictionsPicker, setShowRestrictionsPicker] = useState(false);
  // Snapshot of the row's row_data as loaded, to detect row_data-only edits on submit.
  const originalRowDataRef = useRef<Record<string, any>>({});
  // Snapshot of the row's map_display_config as loaded, to detect style-only edits on submit.
  const originalMapDisplayConfigRef = useRef<Record<string, any>>({});
  // Pending (unsaved) relationship edits made via RelationshipEditDialog. Applied
  // on Confirm & Save so the user can still cancel out of the whole form.
  const [pendingRelEditorState, setPendingRelEditorState] = useState<RelationshipEditorState | null>(null);
  const pendingRelPayloadRef = useRef<IAnchorRelationshipsPatchPayload | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [relatedMedia, setRelatedMedia] = useState<any[]>([]);
  const [displayedMediaCount, setDisplayedMediaCount] = useState(20); // Show 20 initially
  const [isInRelationshipUpdate, setIsInRelationshipUpdate] = useState(false);
  const [hasLocalMediaChanges, setHasLocalMediaChanges] = useState(false);
  const [selectedMediaItem, setSelectedMediaItem] = useState<(RelatedEntityItem & { file?: File }) | null>(null);

  // Dialog states for unlink/delete functionality
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
  const [dialogRelationships, setDialogRelationships] = useState<{
    current: (RelatedEntityItem & { _entityType?: string })[];
    updated: (RelatedEntityItem & { _entityType?: string })[];
  } | null>(null);
  const [deleteMediaItem, setDeleteMediaItem] = useState<MediaItem | null>(null);
  const [mediaItemForUnlink, setMediaItemForUnlink] = useState<MediaItem | null>(null);
  const [uploadMediaDetails, setUploadMediaDetails] = useState<MediaItemDetailed[] | undefined>(undefined);

  // Track newly uploaded media IDs that need retry logic for thumbnail generation
  const [newlyUploadedMediaIds, setNewlyUploadedMediaIds] = useState<Set<string | number>>(new Set());

  // upload progress modals
  const [showUploadProgressModal, setShowUploadProgressModal] = useState(false);

  // ready upload media
  const [uploadMediaList, setUploadMediaList] = useState<File[] | null>();

  // upload action status
  const [isUploadFailed, setIsUploadFailed] = useState(false);
  const [isUploadFinish, setIsUploadFinish] = useState(false);

  const { selectedEndpoint } = useSelectionSelector();
  const { invalidateDataDrawerCache } = useQueryCacheInvalidator();
  const queryClient = useQueryClient();
  const { bumpCmTableLayerVersion } = useMapActions();

  const { uploadFiles, progress: uploadProgress } = useS3Upload();
  const { selectedEntityId } = useSelectionSelector();
  const { setSelectedPrimaryStates } = useSelectionActions();
  const { closePopup } = usePopupActions();

  // track latest thumbnail as user upload new image or select as primary image
  // this is helpful when user uploaded the new image or set new primary image
  // didn't save the form and cancel it, but as latest thumbnail set you want to listen
  // to track it when canceling
  const latestPrimaryThumbnailRef = useRef<string | null>(null);

  // Snapshot of name/description/geometry as loaded, used to detect whether the
  // user actually edited metadata (vs. only relationships/media) on submit.
  const originalMetadataRef = useRef<{ name: string; description: string; geometry: string } | null>(null);

  // Check if this endpoint is a geoentity
  const isGeoEntity =
    endpoint?.entityType === "geo" ||
    ["places", "family-areas", "trips"].includes(endpoint?.tableName || "") ||
    (isCmTableEndpoint(endpoint) && endpoint?.spatialType !== "none");

  // CM table rows, media items and spatial datasets carry a plain `restrictions: [{id, name}]`
  // field, PATCHed directly (see handleSubmit). Edited from the form's Restrictions field
  // or the "Edit Relationships" dialog's Restrictions column — both write the same state.
  const isV2RelEntity = isCmTableEndpoint(endpoint) || endpoint?.entityType === "media" || endpoint?.entityType === "spatial-dataset";

  // Users without `manage_restrictions` never see restrictions at all.
  const showRestrictionsField =
    mode === "edit" &&
    Boolean(currentItem) &&
    isV2RelEntity &&
    canManageRestrictions &&
    config.apiEndpoints.some((ep) => ep.tableName === "restrictions");

  // Names come from the record itself, plus the loaded options for ids picked since.
  const restrictionNameById = useMemo(() => {
    const names = new Map<number, string>();
    ((currentItem as any)?.restrictions ?? []).forEach((r: any) => names.set(Number(r.id), r.name));
    (options?.["restrictions"] ?? []).forEach((o) => names.set(Number(o.id), o.name));
    return names;
  }, [currentItem, options]);

  // Helper function to initialize form with item data (extracted for reuse)
  const initializeFormWithItem = useCallback(
    (itemToInitialize: MediaItem | EntityItem) => {
      console.log("[EntityForm] initializeFormWithItem called for:", itemToInitialize.name, itemToInitialize.id);

      // Initialize form data including geometry for geoentities
      let geometryString = "";
      if (isGeoEntity && itemToInitialize) {
        const geometry = extractGeometryFromItem(itemToInitialize);

        if (geometry.coordinates) {
          geometryString = JSON.stringify(
            {
              type: "Point",
              coordinates: geometry.coordinates
            },
            null,
            2
          );
        } else if (geometry.polygon) {
          geometryString = JSON.stringify(geometry.polygon, null, 2);
        }
      }

      setFormData((prev) => ({
        ...prev,
        name: itemToInitialize.name || "",
        description: itemToInitialize.description || "",
        geometry: geometryString
      }));
      originalMetadataRef.current = {
        name: itemToInitialize.name || "",
        description: itemToInitialize.description || "",
        geometry: geometryString
      };
      setCurrentItem(itemToInitialize);
      // Seed CM-table custom fields from the row's row_data (schema-driven values like `type`).
      setRowData({ ...((itemToInitialize as any)?.row_data ?? {}) });
      originalRowDataRef.current = { ...((itemToInitialize as any)?.row_data ?? {}) };
      // Seed the per-row map styling from the row's own map_display_config.
      setMapDisplayConfig({ ...((itemToInitialize as any)?.map_display_config ?? {}) });
      originalMapDisplayConfigRef.current = { ...((itemToInitialize as any)?.map_display_config ?? {}) };
      console.log("[EntityForm] Form initialized - currentItem set:", itemToInitialize.name, itemToInitialize.id);

      // Initialize "Restrictions" selection from the item's plain `restrictions` field.
      const restrictionIds = ((itemToInitialize as any)?.restrictions ?? []).map((r: any) => Number(r.id));
      setSelectedRestrictionIds(restrictionIds);
      originalRestrictionIdsRef.current = restrictionIds;

      // Extract and set geometry from item if it's a geoentity
      if (isGeoEntity && itemToInitialize) {
        const geometry = extractGeometryFromItem(itemToInitialize);
        console.log("[EntityForm] Extracted geometry from item:", geometry);

        if (
          geometry.coordinates &&
          Array.isArray(geometry.coordinates) &&
          geometry.coordinates.length >= 2 &&
          isFinite(geometry.coordinates[0]) &&
          isFinite(geometry.coordinates[1]) &&
          geometry.coordinates[0] >= -180 &&
          geometry.coordinates[0] <= 180 &&
          geometry.coordinates[1] >= -90 &&
          geometry.coordinates[1] <= 90
        ) {
          setSelectedCoordinates(geometry.coordinates);
          console.log("[EntityForm] Set selectedCoordinates to:", geometry.coordinates);
          setMapData({
            areas: [],
            points: [
              {
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: geometry.coordinates
                },
                properties: {
                  name: itemToInitialize.name || "Current Location",
                  id: "existing-point"
                }
              }
            ]
          });
        } else {
          console.log("[EntityForm] Invalid or missing coordinates, skipping geometry setup:", geometry.coordinates);
        }

        if (geometry.polygon) {
          setSelectedPolygon(geometry.polygon);
          console.log("[EntityForm] Set selectedPolygon to:", geometry.polygon);

          // Determine display name based on geometry type
          const geometryType = (geometry.polygon as any).type;
          const displayName =
            geometryType === "LineString" ? itemToInitialize.name || "Current Route" : itemToInitialize.name || "Current Area";
          const featureId = geometryType === "LineString" ? "existing-route" : "existing-area";

          console.log("[EntityForm] Creating mapData.areas with geometry type:", (geometry.polygon as any).type);

          const areaFeature = {
            type: "Feature" as const,
            geometry: geometry.polygon as any,
            properties: {
              name: displayName,
              id: featureId
            }
          };

          setMapData({
            areas: [areaFeature],
            points: []
          });
        }
      }

      // Extract and set related media for carousel display
      if (itemToInitialize && itemToInitialize.relationships?.media) {
        const primaryImageUrl = (itemToInitialize as any)?.primaryImageUrl;
        const mediaItems = extractMediaItems(itemToInitialize.relationships.media).map((item) => {
          const finalThumbnailUrl = getRelationshipMediaThumbnailUrl(item);

          console.log("[EntityForm] Media item thumbnail generation during init:", {
            itemId: item.id,
            itemName: item.name,
            originalThumbnail: item.entity_thumbnail,
            finalThumbnailUrl
          });

          return {
            ...item,
            ...(primaryImageUrl && (item as any)?.url === primaryImageUrl && { is_primary: true }),
            entity_thumbnail: finalThumbnailUrl
          };
        });
        console.log("[EntityForm] Setting relatedMedia during initialization:", mediaItems.length, "items");
        setRelatedMedia(sortMediaByPrimaryImage(mediaItems));
        setDisplayedMediaCount(20); // Reset to show 20 initially
      } else {
        console.log("[EntityForm] No media relationships found during initialization, clearing relatedMedia");
        setRelatedMedia([]);
        setDisplayedMediaCount(20);
      }
    },
    [isGeoEntity]
  );

  // get typeof geometry of current item/entity
  const geometryType = useMemo(() => {
    if (currentItem as Entity) {
      const entity = currentItem as Entity;
      return getGeometryType(entity?.geometry?.toString());
    }
  }, [currentItem]);

  // NOTE: Options are intentionally NOT eagerly pre-loaded here. The relationship
  // display (RelationshipChips) reads entity names directly from the item's
  // relationship data, and the relationship editor (RelationshipEditDialog ->
  // RelationshipEditorTable) loads its own options on demand when opened. Eagerly
  // fetching every table's options on first edit-form open caused an unwanted flood
  // of API calls (all spatial-datasets + all table rows) the first time the form
  // was opened.

  // Initialize form data when dialog opens or item changes
  useEffect(() => {
    if (open) {
      if (mode === "edit" && existingItem) {
        // Check if we need to fetch relationships for Django items that don't have them loaded
        const checkItemRelationship = hasRelationships(existingItem?.relationships || {});
        // For CM tables always re-fetch: the existingItem from the popup/cache may carry
        // stale or incomplete media relationships due to async L2 refetches in the parent.
        const needsRelationships = endpoint && (!checkItemRelationship || isCmTableEndpoint(endpoint));

        if (needsRelationships) {
          console.log("[EntityForm] Django item missing relationships, fetching Level 3 data:", {
            itemId: existingItem.id,
            itemName: existingItem.name,
            endpointName: endpoint.name
          });

          // Fetch complete item with relationships using the adapter system
          fetchItemWithRelationships(existingItem.id, endpoint, profile)
            .then((completeItem) => {
              console.log("[EntityForm] Successfully fetched complete item with relationships:", {
                itemId: completeItem.id,
                itemName: completeItem.name,
                hasRelationships: !!completeItem.relationships,
                mediaCount: completeItem.relationships?.media?.length || 0
              });
              // Initialize form with complete item data
              initializeFormWithItem(completeItem);
            })
            .catch((error) => {
              console.error("[EntityForm] Failed to fetch complete item data, using existing item:", error);
              // Fallback to existing item if fetch fails
              initializeFormWithItem(existingItem);
            });

          return; // Exit early, initialization will happen in the .then()
        }

        // Prevent re-initialization if we already have a currentItem with the same ID
        // AND we have local changes (selectedCoordinates or modified form data)
        // This prevents overwriting local changes when parent updates existingItem after relationship edits
        if (currentItem && currentItem.id === existingItem.id) {
          const hasLocalCoordinateChanges = selectedCoordinates !== null;
          const hasLocalFormChanges =
            formData.name !== (existingItem.name || "") || formData.description !== (existingItem.description || "");

          // Check if existingItem has newer data than currentItem
          const existingItemTimestamp = new Date(existingItem.last_updated_at || 0).getTime();
          const currentItemTimestamp = new Date(currentItem.last_updated_at || 0).getTime();
          const existingItemIsNewer = existingItemTimestamp > currentItemTimestamp;

          if (hasLocalCoordinateChanges || hasLocalFormChanges || isInRelationshipUpdate || hasLocalMediaChanges || !existingItemIsNewer) {
            console.log("[EntityForm] Skipping re-initialization - preserving local changes for item:", existingItem.id);
            console.log("[EntityForm] hasLocalCoordinateChanges:", hasLocalCoordinateChanges);
            console.log("[EntityForm] hasLocalFormChanges:", hasLocalFormChanges);
            console.log("[EntityForm] isInRelationshipUpdate:", isInRelationshipUpdate);
            console.log("[EntityForm] hasLocalMediaChanges:", hasLocalMediaChanges);
            console.log("[EntityForm] existingItemIsNewer:", existingItemIsNewer);
            console.log("[EntityForm] existingItem timestamp:", existingItem.last_updated_at);
            console.log("[EntityForm] currentItem timestamp:", currentItem.last_updated_at);
            console.log("[EntityForm] selectedCoordinates:", selectedCoordinates);
            return;
          } else {
            console.log("[EntityForm] existingItem has newer data, updating currentItem");
            console.log("[EntityForm] existingItem timestamp:", existingItem.last_updated_at);
            console.log("[EntityForm] currentItem timestamp:", currentItem.last_updated_at);
            console.log("[EntityForm] existingItem relationships:", existingItem.relationships);

            // Update related media when we update currentItem with newer data
            if (existingItem.relationships?.media) {
              const primaryImageUrl = (existingItem as any)?.primaryImageUrl;
              const mediaItems = extractMediaItems(existingItem.relationships.media).map((item) => {
                const finalThumbnailUrl = getRelationshipMediaThumbnailUrl(item);

                console.log("[EntityForm] Media item thumbnail generation:", {
                  itemId: item.id,
                  itemName: item.name,
                  originalThumbnail: item.entity_thumbnail,
                  finalThumbnailUrl
                });

                return {
                  ...item,
                  ...(primaryImageUrl && (item as any)?.url === primaryImageUrl && { is_primary: true }),
                  entity_thumbnail: finalThumbnailUrl
                };
              });
              console.log("[EntityForm] Total media items with thumbnails:", mediaItems.length);
              console.log("[EntityForm] Setting relatedMedia to (existing item update):", mediaItems);
              setRelatedMedia(sortMediaByPrimaryImage(mediaItems));
              setDisplayedMediaCount(20); // Reset to show 20 initially
            }
          }
        }

        // Standard initialization for items that already have relationships or don't need them
        initializeFormWithItem(existingItem);
      } else {
        // Reset for create mode
        resetForm();
      }
      setMapSearchTerm("");
    }
  }, [open, mode, existingItem, isGeoEntity, initializeFormWithItem, endpoint, profile, fetchItemWithRelationships]);

  // Update related media when currentItem changes (for relationship updates)
  useEffect(() => {
    console.log("[EntityForm] currentItem changed, updating related media");
    console.log("[EntityForm] New currentItem:", currentItem?.name, currentItem?.id);
    console.log("[EntityForm] New currentItem geometry:", (currentItem as any)?.geometry);

    if (currentItem && currentItem.relationships?.media && currentItem.relationships.media.length > 0) {
      const primaryImageUrl = (currentItem as any)?.primaryImageUrl;
      const mediaItems = extractMediaItems(currentItem.relationships.media).map((item) => {
        const finalThumbnailUrl = getRelationshipMediaThumbnailUrl(item);

        console.log("[EntityForm] CurrentItem media item thumbnail generation:", {
          itemId: item.id,
          itemName: item.name,
          originalThumbnail: item.entity_thumbnail,
          finalThumbnailUrl
        });

        return {
          ...item,
          ...(primaryImageUrl && (item as any)?.url === primaryImageUrl && { is_primary: true }),
          entity_thumbnail: finalThumbnailUrl
        };
      });
      console.log("[EntityForm] CurrentItem total media items with thumbnails:", mediaItems.length);
      console.log("[EntityForm] Setting relatedMedia to (currentItem update):", mediaItems);
      setRelatedMedia(sortMediaByPrimaryImage(mediaItems));
      setDisplayedMediaCount(20); // Reset to show 20 initially
    } else if (currentItem) {
      // Clear relatedMedia if currentItem has no media relationships
      console.log("[EntityForm] No media relationships found, clearing relatedMedia");
      setRelatedMedia([]);
      setDisplayedMediaCount(20);
    }
  }, [currentItem]);

  // Debug: Track selectedCoordinates changes
  useEffect(() => {
    console.log("[EntityForm] selectedCoordinates changed to:", selectedCoordinates);
  }, [selectedCoordinates]);

  // Debug: Track relatedMedia changes
  useEffect(() => {
    console.log("[EntityForm] relatedMedia state changed to:", {
      length: relatedMedia.length,
      items: relatedMedia.map((item) => ({
        id: item.id,
        name: item.entity_name || item.name,
        entity_thumbnail: item.entity_thumbnail,
        hasThumbnail: !!item.entity_thumbnail
      }))
    });
  }, [relatedMedia]);

  /* ================ MUTATIONS ============== */

  // Entity delete mutation
  const { mutate: entityDeleteMutation, isPending: isEntityDeleting } = useDeleteEntityMutation({
    async onSuccess() {
      console.log(`[EntityForm] Item deleted successfully:`, currentItem?.id);

      if (endpoint && currentItem) {
        // Drop any cached single-row query for the deleted item so it isn't
        // refetched (which would 404 since the row no longer exists)
        const tableSlug = getCmTableSlugFromEndpoint(endpoint) || endpoint.tableName;
        const isDeletedRowQuery = (query: { queryKey: readonly unknown[] }) => {
          const key = query.queryKey;
          return Array.isArray(key) && key[0] === "cm-table-row" && key[1] === tableSlug && String(key[2]) === String(currentItem.id);
        };
        queryClient.cancelQueries({ predicate: isDeletedRowQuery });
        queryClient.removeQueries({ predicate: isDeletedRowQuery });

        // Remove the deleted row from the sidebar list cache, then refetch
        queryClient.setQueriesData({ queryKey: ["drawer-data", endpoint.tableName], exact: false }, (oldData: any) => {
          if (!oldData?.pages) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              data: page.data?.filter((row: any) => String(row.id) !== String(currentItem.id))
            }))
          };
        });
        invalidateDataDrawerCache(endpoint.tableName);

        // Refresh the map layer so the deleted feature disappears immediately.
        if (isCmTableEndpoint(endpoint)) {
          // Bypasses useCmTableMapLayers' "already loaded" guard so it re-fetches the GeoJSON.
          bumpCmTableLayerVersion();
          queryClient.invalidateQueries({ queryKey: ["cm-table-data", tableSlug], exact: false });
        } else if (endpoint.mapLayerId) {
          // Covers config-driven geojson layers too, e.g. Media's "Geotagged Photos" layer.
          refreshLayer(endpoint.mapLayerId);
        }
      }

      // When the deleted entity IS a media item (e.g. deleted from the media
      // preview's pencil-edit form), it may be open in the Layer B preview
      // overlay and/or listed in the currently-selected PARENT entity's Find
      // Related drawer + bottom media drawer. Close the preview and refresh the
      // parent entity's relationships so the deleted media disappears without a
      // reload. The parent primary selection is unchanged by the media edit form.
      if (endpoint?.entityType === "media" && currentItem?.id != null) {
        const deletedMediaId = currentItem.id;
        closeMediaPreviewIfDeleted(deletedMediaId);

        // Only refresh the PARENT entity's relationships when the parent is a
        // DIFFERENT entity than the media we just deleted. When the media itself
        // is the primary selection (e.g. selected from the media data drawer),
        // selectedEntityId === deletedMediaId — invalidating its own entity query
        // would refetch the now-deleted media and surface a "Media {id} not found"
        // 404. The primary-selection clearing below handles that case instead.
        if (selectedEntityId && selectedEndpoint && String(selectedEntityId) !== String(deletedMediaId)) {
          const removeDeletedMedia = (relationships: any) =>
            relationships?.media
              ? { ...relationships, media: relationships.media.filter((m: any) => String(m?.id) !== String(deletedMediaId)) }
              : relationships;

          if (isCmTableEndpoint(selectedEndpoint)) {
            // CM Table parents: the drawers read relationships from the
            // cm-table-row cache (include_relations). Patch it synchronously,
            // then invalidate so it refetches fresh data.
            const parentSlug = getCmTableSlugFromEndpoint(selectedEndpoint) || selectedEndpoint.tableName;
            const parentKey = getCmTableRowQueryKey(parentSlug, selectedEntityId);
            queryClient.setQueryData(parentKey, (old: any) =>
              old?.relationships ? { ...old, relationships: removeDeletedMedia(old.relationships) } : old
            );
            queryClient.invalidateQueries({ queryKey: parentKey, exact: false });
          } else {
            // Non-CM parents: drawers read from the ["entity", parentId] caches.
            queryClient.setQueriesData({ queryKey: ["entity", String(selectedEntityId)], exact: false }, (old: any) =>
              old?.relationships ? { ...old, relationships: removeDeletedMedia(old.relationships) } : old
            );
            queryClient.invalidateQueries({ queryKey: ["entity", String(selectedEntityId)], exact: false });
          }
        }

        // Broadly refresh drawer list caches too (mirrors the upload path).
        invalidateDataDrawerCache();
      }

      // Clear the entity selection if deleting item is the current primary selection, but
      // keep selectedEndpoint so the URL/sidebar stay on the same endpoint (e.g. Family Area)
      // instead of falling back to the default endpoint.
      // Compare as strings — selectedEntityId (from the URL/store) and currentItem.id
      // (from API row data) aren't guaranteed to be the same type (number vs string).
      if (currentItem && String(selectedEntityId) === String(currentItem.id)) {
        setSelectedPrimaryStates({ selectedEntityId: null, showRelationShip: false });
        // The map popup (if open) is showing this now-deleted entity — close it
        // instead of leaving it stuck on a loading skeleton.
        closePopup();
      }

      if (endpoint && currentItem) {
        // Remove the deleted item from cached dropdown options without a refetch
        removeOptionItem(endpoint.tableName, currentItem.id);

        // Notify parent component
        if (onItemDeleted) {
          onItemDeleted(currentItem?.id);
        }

        // Prefer the deleted entity's own name; only fall back to the (singularized)
        // endpoint name if it has none. endsWith("s") guards against truncating names
        // that don't end in a plural "s" (e.g. "Media" -> "Medi").
        const endpointSingularName = endpoint.name.toLowerCase().endsWith("s") ? endpoint.name.slice(0, -1) : endpoint.name;
        enqueueSnackbar(`${currentItem.name || endpointSingularName} deleted successfully`, {
          variant: "success"
        });
      }

      // clear thumbnail tracking if unlink media thumbnail link is same as tracking thumbnail
      if (currentItem?.thumbnail === latestPrimaryThumbnailRef.current) {
        latestPrimaryThumbnailRef.current = null;
      }

      // Close dialogs
      setShowDeleteDialog(false);
      onClose();
    },

    onError(err: Error) {
      const msg = err?.message || "Failed to delete media";
      enqueueSnackbar(msg, { variant: "error" });

      console.error(`[EntityForm] Error deleting item:`, err);
      handleFormSubmissionError(err, enqueueSnackbar, {
        action: "delete",
        itemType: endpoint?.name || "items"
      });
    }
  });

  // Entity media delete mutation
  const { mutate: entityMediaDeleteMutation, isPending: isMediaItemDeleting } = useDeleteEntityMediaMutation({
    onSuccess() {
      setDeleteDialogOpen(false);

      const mediaId = selectedMediaItem?.id?.toString();
      if (mediaId) {
        const deletedMediaId = parseInt(mediaId);

        // CRITICAL: Update the currentItem's relationships to remove the deleted media
        // This is the single source of truth - let the useEffect handle updating relatedMedia
        if (currentItem?.relationships?.media) {
          const updatedCurrentItem = {
            ...currentItem,
            relationships: {
              ...currentItem.relationships,
              media: currentItem.relationships.media.filter((media: any) => media.id !== deletedMediaId)
            }
          };
          setCurrentItem(updatedCurrentItem);

          // Notify parent component of the change
          if (onItemUpdated) {
            onItemUpdated(updatedCurrentItem);
          }

          // Immediately write the updated entity into every cache entry (L1/L2/L3) so all
          // subscribers (Find Related drawer, popup) update synchronously without waiting
          // for the background refetch that invalidateQueries schedules.
          queryClient.setQueriesData({ queryKey: ["entity", String(currentItem?.id)], exact: false }, (old: any) =>
            old ? { ...old, relationships: updatedCurrentItem.relationships } : old
          );
          queryClient.invalidateQueries({ queryKey: ["entity", String(currentItem?.id)], exact: false });
        } else {
          // If no relationships exist, just update the relatedMedia state directly
          setRelatedMedia((prevMedia) => prevMedia.filter((media) => media.id !== deletedMediaId));
        }
      }

      // Clear selection
      setSelectedMediaItem(null);

      // Set flag to indicate we have local media changes
      setHasLocalMediaChanges(true);

      // Close the Layer B media preview if the deleted item was open, and
      // invalidate the drawer caches so the Find Related drawer and bottom
      // media drawer drop the deleted media (mirrors the upload refresh path).
      closeMediaPreviewIfDeleted(mediaId);
      invalidateDataDrawerCache();

      enqueueSnackbar("Media deleted successfully", { variant: "success" });
    },
    onError(e: Error) {
      const msg = e?.message || "Failed to delete media";
      enqueueSnackbar(msg, { variant: "error" });
    }
  });

  // Entity media unlink mutation
  const { mutate: entityMediaUnlinkMutation, isPending: isMediaUnklinkPending } = useEntityMediaUnlinkMutation({
    async onSuccess() {
      setUnlinkDialogOpen(false);

      const selectedMediaItemId = selectedMediaItem?.id;

      if (selectedMediaItem) {
        const unlinkedMediaId = parseInt(selectedMediaItem?.id?.toString());

        // CRITICAL: Update the currentItem's relationships to remove the unlinked media
        // This is the single source of truth - let the useEffect handle updating relatedMedia
        if (currentItem?.relationships?.media) {
          const updatedCurrentItem = {
            ...currentItem,
            relationships: {
              ...currentItem.relationships,
              media: currentItem.relationships.media.filter((media: any) => media.id !== unlinkedMediaId)
            }
          };
          setCurrentItem(updatedCurrentItem);

          // Notify parent component of the change
          if (onItemUpdated) {
            onItemUpdated(updatedCurrentItem);
          }

          // Immediately write the updated entity into every cache entry (L1/L2/L3) so all
          // subscribers (Find Related drawer, popup) update synchronously without waiting
          // for the background refetch that invalidateQueries schedules.
          queryClient.setQueriesData({ queryKey: ["entity", String(currentItem?.id)], exact: false }, (old: any) =>
            old ? { ...old, relationships: updatedCurrentItem.relationships } : old
          );
          queryClient.invalidateQueries({ queryKey: ["entity", String(currentItem?.id)], exact: false });

          // clear thumbnail tracking if unlink media thumbnail link is same as tracking thumbnail
          if (currentItem?.thumbnail === latestPrimaryThumbnailRef.current) {
            latestPrimaryThumbnailRef.current = null;
          }
        } else {
          // If no relationships exist, just update the relatedMedia state directly
          setRelatedMedia((prevMedia) => prevMedia.filter((media) => media.id !== unlinkedMediaId));
        }
      }

      // Clear selection
      setSelectedMediaItem(null);

      // Set flag to indicate we have local media changes
      setHasLocalMediaChanges(true);

      // Close the Layer B media preview if the unlinked item was open, and
      // invalidate the drawer caches so the Find Related drawer and bottom
      // media drawer drop the unlinked media (mirrors the upload refresh path).
      closeMediaPreviewIfDeleted(selectedMediaItemId);
      invalidateDataDrawerCache();

      enqueueSnackbar("Media unlinked successfully", { variant: "success" });
    },

    onError(err: Error) {
      const msg = err?.message || "Failed to unlink media";
      enqueueSnackbar(msg, { variant: "error" });

      console.error(`[EntityForm] Error deleting item:`, err);
    }
  });

  // determine whether item is media entity
  const isEntityTypeMedia = useMemo(() => {
    return (currentItem && getEntityCategory(currentItem as any) === "media") || endpoint?.tableName?.toLowerCase() === "media";
  }, [currentItem?.id, endpoint?.tableName]);

  // Relationships to display in the "Relationships" section: pending (unsaved)
  // edits from RelationshipEditDialog take priority so the user sees their
  // selection immediately, falling back to the server-loaded relationships.
  const displayRelationships = useMemo(() => {
    if (!pendingRelEditorState) return currentItem?.relationships;

    const byTableSlug: Record<string, any[]> = {};
    let media: any[] = [];
    for (const [slug, items] of Object.entries(pendingRelEditorState)) {
      if (slug === "media") media = items as any[];
      else byTableSlug[slug] = items as any[];
    }
    return { related_entities: {}, geo_entities: {}, byTableSlug, media };
  }, [pendingRelEditorState, currentItem?.relationships]);

  // get geometry validation message
  const geoJsonValidationMessage = useMemo(() => {
    if (!formData?.geometry) return "";
    return getGeometryValidationHelperText(formData?.geometry);
  }, [formData?.geometry]);

  const hasInvalidGeoJson = useMemo(() => {
    if (!formData.geometry) return false;
    try {
      const geometry = normaliseGeometryInput(JSON.parse(formData.geometry));

      // Valid FeatureCollection
      if (geometry.type === "FeatureCollection") {
        return !(geometry.features && Array.isArray(geometry.features));
      }

      // Valid Feature
      if (geometry.type === "Feature") {
        return !(geometry.geometry && geometry.geometry.type);
      }

      // Valid basic geometry
      if (!geometry.type || !geometry.coordinates) return true;
      const supportedTypes = ["Point", "LineString", "Polygon", "MultiLineString", "MultiPolygon"];
      return !supportedTypes.includes(geometry.type);
    } catch {
      return true;
    }
  }, [formData.geometry]);

  // Reset form to initial state
  const resetForm = () => {
    console.log("[EntityForm] resetForm called - clearing all state");
    console.log("[EntityForm] Previous selectedCoordinates:", selectedCoordinates);
    console.log("[EntityForm] Previous selectedPolygon:", selectedPolygon);
    setFormData({ name: "", description: "", geometry: "" });
    setSelectedCoordinates(null);
    setSelectedPolygon(null);
    setMapData({ areas: [], points: [] });
    setRowData({});
    rowDataErrorsRef.current = {};
    setShowCaptureErrors(false);
    setMapDisplayConfig({});
    setMapSearchTerm("");
    setCurrentItem(null);
    setIsInRelationshipUpdate(false);
    setSelectedMediaItem(null);
    setHasLocalMediaChanges(false);
    setPendingRelEditorState(null);
    pendingRelPayloadRef.current = null;
    originalMetadataRef.current = null;
    setSelectedRestrictionIds([]);
    originalRestrictionIdsRef.current = [];
    originalRowDataRef.current = {};
    originalMapDisplayConfigRef.current = {};
  };

  // Handle form field changes
  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    // If geometry field is being updated, try to parse and update map
    if (name === "geometry" && isGeoEntity && value.trim()) {
      handleGeometryChange(value);
    }
  }

  // Handle geometry changes from text input
  const handleGeometryChange = (geometryString: string) => {
    try {
      if (!geometryString.trim()) {
        // Clear geometry
        setSelectedCoordinates(null);
        setSelectedPolygon(null);
        setMapData({ areas: [], points: [] });
        return;
      }

      const geometry = normaliseGeometryInput(JSON.parse(geometryString));

      // Handle Feature Collections
      if (geometry.type === "FeatureCollection" && geometry.features && Array.isArray(geometry.features)) {
        // For Feature Collections, extract all geometries and display them
        const allGeometries = geometry.features
          .map((feature: any) => feature.geometry)
          .filter((geom: any) => geom && geom.type && geom.coordinates);

        if (allGeometries.length === 0) {
          throw new Error("FeatureCollection contains no valid geometries");
        }

        // Create map features for all geometries in the collection
        const mapFeatures = geometry.features.map((feature: any, index: number) => ({
          type: "Feature" as const,
          geometry: feature.geometry,
          properties: {
            name: feature.properties?.name || `Feature ${index + 1}`,
            id: `feature-${index}`,
            ...feature.properties
          }
        }));

        // Clear coordinates and set all features as areas
        setSelectedCoordinates(null);
        setSelectedPolygon(geometry); // Store the whole FeatureCollection
        setMapData({
          areas: mapFeatures,
          points: []
        });

        console.log("[EntityForm] Loaded FeatureCollection with", allGeometries.length, "features");
        return;
      }

      // Handle individual Features
      if (geometry.type === "Feature" && geometry.geometry) {
        // Extract the geometry from the Feature
        const actualGeometry = geometry.geometry;
        setSelectedCoordinates(null);
        setSelectedPolygon(actualGeometry);
        setMapData({
          areas: [
            {
              type: "Feature",
              geometry: actualGeometry,
              properties: {
                name: geometry.properties?.name || "Feature Geometry",
                id: "feature-geometry",
                ...geometry.properties
              }
            }
          ],
          points: []
        });
        return;
      }

      // Validate basic GeoJSON Geometry structure
      if (!geometry.type || !geometry.coordinates) {
        throw new Error("Invalid GeoJSON: missing type or coordinates");
      }

      // Handle different geometry types
      if (geometry.type === "Point" && Array.isArray(geometry.coordinates) && geometry.coordinates.length >= 2) {
        const coords: [number, number] = [geometry.coordinates[0], geometry.coordinates[1]];
        setSelectedCoordinates(coords);
        setSelectedPolygon(null);

        // Update map data
        setMapData({
          areas: [],
          points: [
            {
              type: "Feature",
              geometry: geometry,
              properties: {
                name: formData.name || "Edited Location",
                id: "edited-point"
              }
            }
          ]
        });
      } else if (["Polygon", "MultiPolygon", "LineString", "MultiLineString"].includes(geometry.type)) {
        setSelectedCoordinates(null);
        setSelectedPolygon(geometry);

        // Determine display name based on geometry type
        const displayName = ["LineString", "MultiLineString"].includes(geometry.type)
          ? formData.name || "Edited Route"
          : formData.name || "Edited Area";
        const featureId = ["LineString", "MultiLineString"].includes(geometry.type) ? "edited-route" : "edited-area";

        // Update map data
        setMapData({
          areas: [
            {
              type: "Feature",
              geometry: geometry,
              properties: {
                name: displayName,
                id: featureId
              }
            }
          ],
          points: []
        });
      } else {
        throw new Error(`Unsupported geometry type: ${geometry.type}`);
      }

      console.log("[EntityForm] Geometry updated from text input:", geometry.type);
    } catch (error) {
      console.warn("[EntityForm] Invalid GeoJSON in geometry field:", error);
      // Don't clear the map on invalid JSON - let user continue editing
    }
  };

  // Map click handler to set coordinates (only for point geometries)
  const handleMapClick = useCallback(
    (event: any) => {
      if (!isGeoEntity || endpoint?.tableName !== "places") return;

      const { lng, lat } = event.lngLat;
      const coordinates: [number, number] = [lng, lat];
      console.log("[EntityForm] Map clicked, setting coordinates:", coordinates);
      console.log("[EntityForm] Previous selectedCoordinates:", selectedCoordinates);
      setSelectedCoordinates(coordinates);

      console.log("[EntityForm] Map clicked, coordinates set:", coordinates);

      // Update geometry text field
      const pointGeometry = {
        type: "Point" as const,
        coordinates: coordinates as [number, number]
      };

      setFormData((prev) => ({
        ...prev,
        geometry: JSON.stringify(pointGeometry, null, 2)
      }));

      // Update map data to show the selected point
      setMapData({
        areas: [],
        points: [
          {
            type: "Feature",
            geometry: pointGeometry,
            properties: {
              name: mode === "edit" ? "Updated Location" : "Selected Location",
              id: "temp-point"
            }
          }
        ]
      });
    },
    [isGeoEntity, endpoint?.tableName, mode, selectedCoordinates]
  );

  // Handle coordinate search (fly to location)
  const handleCoordinateSearch = useCallback(
    async (searchTerm: string) => {
      console.log("[EntityForm] Coordinate search:", searchTerm);

      // Simple coordinate parsing (latitude, longitude)
      const coordinateRegex = /^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/;
      const match = searchTerm.trim().match(coordinateRegex);

      if (match) {
        const lat = parseFloat(match[1]);
        const lng = parseFloat(match[2]);

        if (lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90) {
          const coordinates: [number, number] = [lng, lat];
          setSelectedCoordinates(coordinates);

          // Update geometry text field
          const pointGeometry = {
            type: "Point" as const,
            coordinates: coordinates as [number, number]
          };

          setFormData((prev) => ({
            ...prev,
            geometry: JSON.stringify(pointGeometry, null, 2)
          }));

          // Update map data
          setMapData({
            areas: [],
            points: [
              {
                type: "Feature",
                geometry: pointGeometry,
                properties: {
                  name: "Searched Location",
                  id: "searched-point"
                }
              }
            ]
          });

          enqueueSnackbar(`Moved to coordinates: ${lat}, ${lng}`, { variant: "success" });
          return [];
        }
      }

      enqueueSnackbar("Invalid coordinates. Use format: latitude, longitude (e.g., -25.2744, 133.7751)", {
        variant: "warning"
      });
      return [];
    },
    [enqueueSnackbar]
  );

  // Clear coordinates
  const handleClearCoordinates = () => {
    setSelectedCoordinates(null);
    setMapData({ areas: [], points: [] });
    setFormData((prev) => ({
      ...prev,
      geometry: ""
    }));
  };

  // Handle Edit Relationships button click
  const handleEditRelationships = () => {
    if (!currentItem) return;
    console.log("[EntityForm] Opening RelationshipEditDialog - setting isInRelationshipUpdate flag");
    setIsInRelationshipUpdate(true);
    setShowEditRelationships(true);
  };

  // Memoize autoZoom calculation
  const shouldAutoZoom = useMemo(() => {
    return mapData.points.length > 0 || mapData.areas.length > 0;
  }, [mapData.points.length, mapData.areas.length]);

  // Memoize initial view state
  const initialViewState = useMemo(() => resolveMapView(config.initialMapView), [config.initialMapView]);

  // Resolve the preview color/icon so the draw map reflects the row's chosen
  // styling live. The row's per-row `map_display_config` references a table style
  // choice by value (e.g. `{ type: "camp" }`); the table-level style resolves
  // that value to the icon/colour. Falls back to the table default when the row
  // has no per-row selection. Color drives polygon/line preview; icon drives points.
  const cmPreviewOutput = useMemo(() => {
    if (!endpoint || !isCmTableEndpoint(endpoint)) return undefined;
    const styleKey = endpoint.styleKey || getStyleFieldKey(endpoint.style, endpoint.spatialType);
    const rowValue = getRowStyleValue(mapDisplayConfig, styleKey);
    return resolveStyleOutputForValue(endpoint.style, endpoint.spatialType, rowValue);
  }, [endpoint, mapDisplayConfig]);
  const cmPreviewColor = useMemo(
    () => (endpoint && isCmTableEndpoint(endpoint) && !isIconStyled(endpoint.spatialType) ? cmPreviewOutput : undefined),
    [endpoint, cmPreviewOutput]
  );
  const cmPreviewIcon = useMemo(
    () => (endpoint && isCmTableEndpoint(endpoint) && isIconStyled(endpoint.spatialType) ? resolveIconUrl(cmPreviewOutput) : undefined),
    [endpoint, cmPreviewOutput]
  );

  // Handle form submission
  const handleSubmit = async () => {
    if (!endpoint) return;

    // A form-supplied title isn't typed here, so there is no Name input to complain
    // about: an empty one means its Title Fields are empty, which the capture
    // validation below reports against the actual fields. If they are all optional
    // and left blank, the row is saved unnamed and the backend applies its
    // `<form name> + <uuid>` fallback.
    if (!formSuppliesName && !formData.name.trim()) {
      enqueueSnackbar("Name is required", { variant: "error" });
      return;
    }

    // Block the save when a required form field is empty or a value is invalid.
    if (isCmTableEndpoint(endpoint)) {
      const errorCount = Object.keys(rowDataErrorsRef.current).length;
      if (errorCount > 0) {
        setShowCaptureErrors(true);
        scrollToFirstCaptureError();
        enqueueSnackbar(
          errorCount === 1
            ? "Please complete the required form field before saving."
            : `Please complete ${errorCount} required form fields before saving.`,
          { variant: "error" }
        );
        return;
      }
    }

    // For geoentities, check if geometry is required based on entity type
    if (isGeoEntity) {
      if (endpoint.tableName === "places" && !selectedCoordinates) {
        enqueueSnackbar("Please set a location by clicking on the map", { variant: "error" });
        return;
      }

      // CM table rows with a spatial type can be saved without geometry (it can be
      // added later) - just warn the user rather than blocking the save.
      if (isCmTableEndpoint(endpoint) && endpoint.spatialType && endpoint.spatialType !== "none" && !formData.geometry?.trim()) {
        enqueueSnackbar("Saved without a location/area. You can add geometry for this record later.", { variant: "warning" });
      }
    }

    try {
      setIsSubmitting(true);

      // CM table rows and media items use the dedicated v2 anchor-relationships API
      // for relationships, and only need a row/item PATCH when name, description or
      // geometry actually changed.
      let metadataChanged = true;
      if (mode === "edit" && isV2RelEntity && originalMetadataRef.current) {
        const orig = originalMetadataRef.current;
        metadataChanged =
          formData.name.trim() !== orig.name ||
          formData.description.trim() !== orig.description ||
          (formData.geometry?.trim() ?? "") !== orig.geometry;
      }

      // "Restrictions" is a plain field PATCHed alongside name/description/geometry,
      // so a restrictions-only edit must still trigger the row/item PATCH.
      let restrictionsChanged = false;
      if (mode === "edit" && isV2RelEntity) {
        const sortedSelected = [...selectedRestrictionIds].sort((a, b) => a - b);
        const sortedOriginal = [...originalRestrictionIdsRef.current].sort((a, b) => a - b);
        restrictionsChanged =
          sortedSelected.length !== sortedOriginal.length || sortedSelected.some((id, idx) => id !== sortedOriginal[idx]);
      }

      // CM-table row_data (schema fields like `type`) is PATCHed with the row, so a
      // row_data-only edit must also trigger the PATCH.
      let rowDataChanged = false;
      let mapDisplayConfigChanged = false;
      if (mode === "edit" && isCmTableEndpoint(endpoint)) {
        const stableStringify = (obj: Record<string, any>) =>
          JSON.stringify(
            Object.keys(obj)
              .sort()
              .reduce((acc: Record<string, any>, k) => ((acc[k] = obj[k]), acc), {})
          );
        rowDataChanged = stableStringify(rowData) !== stableStringify(originalRowDataRef.current);
        mapDisplayConfigChanged = stableStringify(mapDisplayConfig) !== stableStringify(originalMapDisplayConfigRef.current);
      }

      const shouldPatchMetadata =
        mode === "create" || !isV2RelEntity || metadataChanged || restrictionsChanged || rowDataChanged || mapDisplayConfigChanged;

      let response: any = currentItem;
      let entityData: any = currentItem;

      if (shouldPatchMetadata) {
        let url: string;
        let method: string;

        if (mode === "edit" && currentItem) {
          url = buildItemDetailUrl(endpoint.api_endpoint, currentItem.id);
          method = "PATCH";
        } else {
          url = `${REACT_APP_API_ROOT}${endpoint.api_endpoint}`;
          method = "POST";
        }

        // A form-supplied title that is still empty (every Title Field optional and
        // blank) is left off the payload so the backend applies its own
        // `<form name> + <uuid>` fallback rather than storing "".
        const trimmedName = formData.name.trim();
        const payload: any = {};
        if (trimmedName || !formSuppliesName) {
          payload.name = trimmedName;
        }

        // Send the description when it has content, and also when an edit cleared a
        // description the row previously had — otherwise emptying the form's
        // description field would look saved but never reach the row.
        const trimmedDescription = formData.description.trim();
        if (trimmedDescription) {
          payload.description = trimmedDescription;
        } else if (mode === "edit" && (originalMetadataRef.current?.description ?? "").trim()) {
          payload.description = "";
        }

        // Add geometry for geoentities
        if (isGeoEntity && formData.geometry && formData.geometry.trim()) {
          try {
            const geometry = normaliseGeometryInput(JSON.parse(formData.geometry));

            let finalGeometry = geometry;

            // Convert FeatureCollection to single geometry (PostGIS doesn't support FeatureCollections)
            if (geometry.type === "FeatureCollection" && geometry.features && Array.isArray(geometry.features)) {
              if (geometry.features.length === 0) {
                throw new Error("FeatureCollection is empty");
              }

              if (geometry.features.length === 1) {
                // Single feature - extract its geometry
                finalGeometry = geometry.features[0].geometry;
                console.log("[EntityForm] Extracted single geometry from FeatureCollection:", finalGeometry.type);
              } else {
                // Multiple features - try to combine intelligently based on geometry types
                const geometries = geometry.features.map((feature: any) => feature.geometry);
                const geometryTypes = [...new Set(geometries.map((g: any) => g.type))];

                // If all geometries are the same type, try to combine them into a Multi* type
                if (geometryTypes.length === 1) {
                  const singleType = geometryTypes[0];

                  if (singleType === "LineString") {
                    // Combine LineStrings into MultiLineString
                    finalGeometry = {
                      type: "MultiLineString",
                      coordinates: geometries.map((g: any) => g.coordinates)
                    };
                    console.log("[EntityForm] Combined", geometries.length, "LineStrings into MultiLineString");
                  } else if (singleType === "Polygon") {
                    // Combine Polygons into MultiPolygon
                    finalGeometry = {
                      type: "MultiPolygon",
                      coordinates: geometries.map((g: any) => g.coordinates)
                    };
                    console.log("[EntityForm] Combined", geometries.length, "Polygons into MultiPolygon");
                  } else if (singleType === "Point") {
                    // Combine Points into MultiPoint
                    finalGeometry = {
                      type: "MultiPoint",
                      coordinates: geometries.map((g: any) => g.coordinates)
                    };
                    console.log("[EntityForm] Combined", geometries.length, "Points into MultiPoint");
                  } else if (singleType === "MultiLineString") {
                    // Flatten MultiLineStrings into a single MultiLineString
                    const allCoordinates = geometries.flatMap((g: any) => g.coordinates);
                    finalGeometry = {
                      type: "MultiLineString",
                      coordinates: allCoordinates
                    };
                    console.log(
                      "[EntityForm] Flattened",
                      geometries.length,
                      "MultiLineStrings into single MultiLineString with",
                      allCoordinates.length,
                      "line segments"
                    );
                  } else if (singleType === "MultiPolygon") {
                    // Flatten MultiPolygons into a single MultiPolygon
                    const allCoordinates = geometries.flatMap((g: any) => g.coordinates);
                    finalGeometry = {
                      type: "MultiPolygon",
                      coordinates: allCoordinates
                    };
                    console.log("[EntityForm] Flattened", geometries.length, "MultiPolygons into single MultiPolygon");
                  } else if (singleType === "MultiPoint") {
                    // Flatten MultiPoints into a single MultiPoint
                    const allCoordinates = geometries.flatMap((g: any) => g.coordinates);
                    finalGeometry = {
                      type: "MultiPoint",
                      coordinates: allCoordinates
                    };
                    console.log("[EntityForm] Flattened", geometries.length, "MultiPoints into single MultiPoint");
                  } else {
                    // For other types, fall back to GeometryCollection
                    finalGeometry = {
                      type: "GeometryCollection",
                      geometries: geometries
                    };
                    console.log("[EntityForm] Combined", geometries.length, "complex geometries into GeometryCollection");
                  }
                } else {
                  // Mixed geometry types - use GeometryCollection
                  finalGeometry = {
                    type: "GeometryCollection",
                    geometries: geometries
                  };
                  console.log("[EntityForm] Combined", geometries.length, "mixed geometry types into GeometryCollection");
                }
              }
            }
            // Extract geometry from Feature wrapper
            else if (geometry.type === "Feature" && geometry.geometry) {
              finalGeometry = geometry.geometry;
              console.log("[EntityForm] Extracted geometry from Feature:", finalGeometry.type);
            }

            // Validate final geometry structure
            const isValidGeometry =
              (finalGeometry.type &&
                finalGeometry.coordinates &&
                ["Point", "MultiPoint", "LineString", "Polygon", "MultiLineString", "MultiPolygon"].includes(finalGeometry.type)) ||
              (finalGeometry.type === "GeometryCollection" && finalGeometry.geometries);

            if (isValidGeometry) {
              // CM tables enforce the geometry type matches the table's spatial type
              // (e.g. a "point" table can't be saved with a Polygon), even though
              // geometry itself is optional.
              if (isCmTableEndpoint(endpoint) && endpoint.spatialType && endpoint.spatialType !== "none") {
                const mismatchError = getGeometryTypeMismatchError(finalGeometry.type, endpoint.spatialType);
                if (mismatchError) {
                  enqueueSnackbar(mismatchError, { variant: "error" });
                  return;
                }
              }

              // Strip Z dimensions to ensure 2D compatibility with PostGIS
              const geometry2D = stripZDimensions(finalGeometry);
              payload.geometry = geometry2D;
              console.log("[EntityForm] Adding geometry to payload:", geometry2D.type);
            } else {
              throw new Error("Invalid geometry structure after processing");
            }
          } catch (error) {
            console.warn("[EntityForm] Invalid geometry JSON, skipping:", error);
            enqueueSnackbar("Invalid geometry format. Please check your GeoJSON.", { variant: "warning" });
            return; // Don't submit with invalid geometry
          }
        } else if (isGeoEntity && endpoint.tableName === "places" && selectedCoordinates) {
          // Fallback for places using the old coordinate system
          payload.geometry = {
            type: "Point",
            coordinates: selectedCoordinates
          };
        }

        // Add relationships if they exist (edit mode with updated relationships).
        // CM table rows and media items use the dedicated v2 relationships API;
        // including relationships in their PATCH payload would 400.
        if (
          mode === "edit" &&
          currentItem?.relationships &&
          !isCmTableEndpoint(endpoint) &&
          endpoint.entityType !== "media" &&
          endpoint.entityType !== "spatial-dataset"
        ) {
          // Ensure relationships have proper structure for transformRelationships
          const relationshipsToTransform = {
            related_entities: currentItem.relationships.related_entities || {},
            geo_entities: currentItem.relationships.geo_entities || {},
            media: relatedMedia || currentItem.relationships.media || []
          };

          const builtRelationships = buildRelationshipForPayload(relationshipsToTransform, config.apiEndpoints);
          const transformedRelationships = transformRelationships(builtRelationships, config);
          if (transformedRelationships) {
            payload.relationships = transformedRelationships;
          }
        }

        // Add primary image if one was selected
        if (mode === "edit") {
          const findPrimaryImage = relatedMedia?.find((media) => media?.is_primary);
          if (findPrimaryImage) {
            payload.primary_image_write = {
              id: parseInt(findPrimaryImage?.id?.toString())
            };
          }
        }

        // "Restrictions" is a plain `restrictions: number[]` field on CM table rows
        // and media items, PATCHed directly (not via anchor-relationships).
        // Only include when the user actually changed the restrictions selection, to avoid 403 error.
        if (mode === "edit" && isV2RelEntity && restrictionsChanged) {
          payload.restrictions = selectedRestrictionIds;
        }

        // CM-table schema fields (`site_code`, …) are sent as `row_data`.
        // On create always include it (may be empty); on edit only when changed.
        if (isCmTableEndpoint(endpoint) && (mode === "create" || rowDataChanged)) {
          payload.row_data = rowData;
        }

        // Tag the row with the form version its inputs were rendered from, so future
        // edits resolve the exact schema that produced it. The backend stamps
        // `_form_uuid`/`_form_group_id` into `row_data` from this `form_uuid`.
        if (isCmTableEndpoint(endpoint) && resolvedForm.activeForm?.uuid) {
          payload.form_uuid = resolvedForm.activeForm.uuid;
        }

        // Per-row map styling lives on the row's own `map_display_config` (icon for
        // points, colour for polygon/line), NOT inside row_data. On create include it
        // when the user picked a style; on edit only when it changed.
        if (isCmTableEndpoint(endpoint)) {
          const hasStyle = mapDisplayConfig && Object.keys(mapDisplayConfig).length > 0;
          if ((mode === "create" && hasStyle) || (mode === "edit" && mapDisplayConfigChanged)) {
            payload.map_display_config = mapDisplayConfig;
          }
        }

        response = await apiFetch(url, {
          method,
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        });

        console.log(`[EntityForm] Item ${mode === "edit" ? "updated" : "created"} successfully:`, response);

        // CM-Table create backend wraps the row: {message: "...", data: {...row}}.
        // All other paths (v1 create, any edit) fall through to response directly.
        entityData = mode === "create" && isCmTableEndpoint(endpoint) && response?.data ? response.data : response;

        if (mode === "create" && uploadMediaList?.length) {
          await handleMediaUpload(uploadMediaList, entityData);
        }

        // Update OptionsContext for the created/updated item (so changes appear in dropdowns
        // immediately) without refetching the entire table.
        const optionItemId = mode === "create" ? entityData?.id : currentItem?.id;
        if (optionItemId !== undefined && optionItemId !== null) {
          // The backend may (re)derive the name from the form's Title Fields, so the
          // saved row's name wins over the one computed here.
          upsertOptionItem(endpoint.tableName, { id: optionItemId, name: entityData?.name || trimmedName });
        }
      } // end shouldPatchMetadata

      // Apply pending relationship changes (Edit Relationships dialog) now that the
      // user has confirmed the overall save.
      if (mode === "edit" && currentItem && isV2RelEntity && pendingRelPayloadRef.current) {
        const { create, delete: del } = pendingRelPayloadRef.current;
        if (create.length || del.length) {
          const anchorType: AnchorType =
            endpoint.entityType === "media" ? "media" : endpoint.entityType === "spatial-dataset" ? "spatialdataset" : "cmtabledata";
          await patchAnchorRelationships(anchorType, currentItem.id, pendingRelPayloadRef.current);
        }
      }

      // The PATCH response omits relationships unless include_relations=true is added.
      // Re-fetch the complete row/item so the parent callback and local state carry
      // full relationship data, ensuring the sidebar/map and next dialog open show
      // the latest relationships, geometry and media without a page reload.
      let itemForUpdate = response;
      if (mode === "edit" && currentItem && isV2RelEntity) {
        if (isCmTableEndpoint(endpoint)) {
          const tableSlug = getCmTableSlugFromEndpoint(endpoint);
          if (tableSlug) {
            try {
              const freshRowRes = await getCmTableRow(tableSlug, currentItem.id, { include_relations: true });
              itemForUpdate = freshRowRes.data as any;
              // useSelectedEntity's L2 fallback caches the row here once relationships have
              // been fetched (e.g. via the "Find related" / info panel). That cache is the
              // source of truth for "Currently Viewing" and the map popup, so it must be
              // refreshed too — otherwise they keep showing the pre-edit name/image.
              queryClient.setQueryData(getCmTableRowQueryKey(tableSlug, currentItem.id), freshRowRes.data);
              // Await the geo refetch so GeoJSON features carry the new thumbnail
              // before the dialog closes and the popup can be re-clicked.
              await queryClient.refetchQueries({ queryKey: ["cm-table-geo", tableSlug], exact: false, type: "active" });
              queryClient.invalidateQueries({ queryKey: ["cm-table-data", tableSlug], exact: false });
            } catch (refetchError) {
              console.warn("[EntityForm] Failed to refetch CM Table row after edit:", refetchError);
            }
          }
        } else if (endpoint.entityType === "media") {
          try {
            // Media detail responses always include `relationships` — no query param needed.
            itemForUpdate = await apiFetch(`${REACT_APP_API_ROOT}api/v2/media/${currentItem.id}/`);
            // useSelectedEntity reads relationships from the L2/L3 ["entity",...] cache for
            // non-CM-table entities — patch it so the Find Related drawer / media strip pick
            // up the change too, not just the popup (via onItemUpdated below).
            queryClient.setQueriesData({ queryKey: ["entity", String(currentItem.id)], exact: false }, (old: any) =>
              old ? { ...old, relationships: (itemForUpdate as any)?.relationships } : old
            );
          } catch (refetchError) {
            console.warn("[EntityForm] Failed to refetch media item after edit:", refetchError);
          }
        }
      }

      // Notify parent component
      if (mode === "edit" && onItemUpdated) {
        onItemUpdated(itemForUpdate);
      }
      // if upload media list is not empty
      // don't invoke `onItemCreated` callback
      // because upload progress modal could be open and we don't want to close
      // it, till explicitly closed by the user and then we can call call-back
      // otherwise, we can immediately call it
      else if (mode === "create" && onItemCreated && !uploadMediaList?.length) {
        onItemCreated(entityData);
      }

      // Update local state (edit uses re-fetched row; create uses the normalised entity)
      setCurrentItem(mode === "edit" ? itemForUpdate : entityData);

      if (mode === "edit") {
        // Clear selected media after successful update
        setSelectedMediaItem(null);
        // Clear local media changes flag after successful update
        setHasLocalMediaChanges(false);
        // Clear pending relationship edits now that they've been applied
        setPendingRelEditorState(null);
        pendingRelPayloadRef.current = null;
        // Re-baseline restrictions so re-opening this dialog without re-fetch
        // doesn't show a stale "changed" state.
        originalRestrictionIdsRef.current = [...selectedRestrictionIds];
        // Re-baseline row_data too, for the same reason.
        originalRowDataRef.current = { ...rowData };
      }

      // Only singularise names that genuinely end in a plural "s" (e.g. "Cultural Sites" → "Cultural Site").
      // Unconditional slice trims names like "MultiLineString" → "MultiLineStrin".
      const _singularName = endpoint.name.toLowerCase().endsWith("s") ? endpoint.name.slice(0, -1) : endpoint.name;
      enqueueSnackbar(`${_singularName} ${mode === "edit" ? "updated" : "created"} successfully`, {
        variant: "success"
      });

      // Refresh the map layer if this endpoint has a corresponding config-driven map layer
      if (endpoint.mapLayerId) {
        console.log(`[EntityForm] Scheduling map layer refresh: ${endpoint.mapLayerId}`);

        setTimeout(() => {
          console.log(`[EntityForm] Refreshing map layer: ${endpoint.mapLayerId}`);
          refreshLayer(endpoint.mapLayerId!);
        }, 750);
      }

      // invalidate data drawer list and trigger refetch
      invalidateDataDrawerCache(endpoint.tableName);

      // For CM tables (create or edit): bump the layer version so useCmTableMapLayers
      // bypasses its "already loaded" guard and re-fetches the GeoJSON — this updates
      // the map geometry and, indirectly, the popup feature data.
      if (isCmTableEndpoint(endpoint)) {
        bumpCmTableLayerVersion();
        if (mode === "create") {
          const tableSlug = getCmTableSlugFromEndpoint(endpoint);
          if (tableSlug) {
            queryClient.invalidateQueries({ queryKey: ["cm-table-data", tableSlug], exact: false });
          }
        }
      }

      // Reset form and close dialog
      if (mode !== "create" && !uploadMediaList?.length) {
        resetForm();
        onClose();
      }
    } catch (error) {
      console.error(`[EntityForm] Error ${mode === "edit" ? "updating" : "creating"} item:`, error);

      handleFormSubmissionError(error, enqueueSnackbar, {
        action: mode === "edit" ? "edit" : "create",
        itemType: endpoint?.name || "items"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    onClose();

    if (currentItem) {
      onCancel?.({ ...currentItem, thumbnail: latestPrimaryThumbnailRef.current || currentItem?.thumbnail });
    } else {
      onCancel?.();
    }
  };

  // Handle item deletion
  const handleDelete = async () => {
    if (!endpoint || !currentItem || mode !== "edit") return;

    try {
      entityDeleteMutation({
        id: currentItem?.id,
        apiEndpointUrl: endpoint?.api_endpoint
      });
    } catch (error: any) {
      console.error(`[EntityForm] Error deleting item:`, error);
      const msg = error?.message || "Failed to delete item";
      enqueueSnackbar(msg);
    }
  };

  // Handle set primary image (local state only - actual update happens in form submission)
  const handleSetPrimaryImage = async () => {
    if (!selectedMediaItem) return;

    if (selectedMediaItem?.is_primary) return;

    let shouldSetPrimary = true;

    const mediaType = getMediaType(selectedMediaItem as MediaResponse);
    if (mediaType === "image") {
      shouldSetPrimary = true;
    } else if (selectedMediaItem?.file) {
      shouldSetPrimary = isAFile(selectedMediaItem?.file, "image");
    } else {
      shouldSetPrimary = getMediaType(selectedMediaItem?.entity_thumbnail || (selectedMediaItem as MediaResponse)) === "image";
    }

    // set as primary media only if selected media is image
    if (shouldSetPrimary) {
      // Snapshot state before optimistic update so revert is correct on failure
      const prevRelatedMedia = relatedMedia;
      const prevSelectedMediaItem = selectedMediaItem;

      const updatedMediaList = updatePrimaryMedia(relatedMedia, selectedMediaItem?.id?.toString());
      setRelatedMedia(updatedMediaList);
      setSelectedMediaItem({ ...selectedMediaItem, is_primary: true });

      // For CM tables, call the v2 set-primary-image API immediately instead of
      // deferring to handleSubmit, since the PATCH endpoint uses a different mechanism.
      if (isCmTableEndpoint(endpoint) && currentItem) {
        try {
          const mediaId = parseInt(String(selectedMediaItem.id));
          if (!Number.isFinite(mediaId)) throw new Error("Invalid media ID");
          await setCmTablePrimaryImage(mediaId, currentItem.id);
          enqueueSnackbar("Primary image updated", { variant: "success" });
        } catch {
          enqueueSnackbar("Failed to set primary image", { variant: "error" });
          // Revert to snapshotted state
          setRelatedMedia(prevRelatedMedia);
          setSelectedMediaItem(prevSelectedMediaItem);
          return;
        }
      } else if (endpoint?.entityType === "spatial-dataset" && currentItem) {
        try {
          const mediaId = parseInt(String(selectedMediaItem.id));
          if (!Number.isFinite(mediaId)) throw new Error("Invalid media ID");
          await setSpatialDatasetPrimaryImage(mediaId, currentItem.id);
          enqueueSnackbar("Primary image updated", { variant: "success" });
        } catch {
          enqueueSnackbar("Failed to set primary image", { variant: "error" });
          setRelatedMedia(prevRelatedMedia);
          setSelectedMediaItem(prevSelectedMediaItem);
          return;
        }
      } else {
        enqueueSnackbar("Primary image will be set when you click Update", {
          variant: "info"
        });
      }
    } else {
      enqueueSnackbar("Primary media can only be image.", {
        variant: "warning"
      });
    }
  };

  function handleSelectMedia(item: RelatedEntityItem | MediaItem) {
    setSelectedMediaItem(item);
  }

  const handleLoadMoreMedia = () => {
    setDisplayedMediaCount((prev) => Math.min(prev + 20, relatedMedia.length));
  };

  // Prepare unlink media (similar to DataItemDetails.tsx)
  const prepareUnlink = async (mediaId: number) => {
    try {
      // check weather media is local,
      // if media is local, then show message and abort
      const isLocalMedia = relatedMedia?.some((media) => media?.id?.toString() === mediaId?.toString() && media?.file);
      if (isLocalMedia) {
        enqueueSnackbar("The media have no relationships.", { variant: "warning" });
        return;
      }

      // Find the media endpoint from config
      const mediaEndpoint = config.apiEndpoints.find((endpoint) => endpoint.tableName === "media");
      if (!mediaEndpoint) {
        throw new Error("Media endpoint not found in configuration");
      }

      // Fetch the current media item with relationships using the generalized system
      const mediaItem = await fetchItemWithRelationships(mediaId, mediaEndpoint, profile);

      console.log("[EntityForm] Media item relationships:", mediaItem.relationships);
      console.log("[EntityForm] Current endpoint relationship key:", endpoint?.relationshipKey);
      console.log("[EntityForm] Current item ID:", currentItem?.id);

      // Show ALL relationships this media item has, not just the current endpoint type
      let allCurrentRels: (RelatedEntityItem & { _entityType?: string })[] = [];

      // Collect all relationships from all sections, preserving entity type information
      if (mediaItem.relationships) {
        // Add all related_entities relationships
        if (mediaItem.relationships.related_entities) {
          Object.entries(mediaItem.relationships.related_entities).forEach(([key, entities]) => {
            const entitiesWithType = (entities as RelatedEntityItem[]).map((entity) => ({
              ...entity,
              _entityType: key // Preserve the entity type for filtering
            }));
            allCurrentRels.push(...entitiesWithType);
          });
        }

        // Add all geo_entities relationships
        if (mediaItem.relationships.geo_entities) {
          Object.entries(mediaItem.relationships.geo_entities).forEach(([key, entities]) => {
            const entitiesWithType = (entities as RelatedEntityItem[]).map((entity) => ({
              ...entity,
              _entityType: key // Preserve the entity type for filtering
            }));
            allCurrentRels.push(...entitiesWithType);
          });
        }

        // Add media relationships if they exist
        if (mediaItem.relationships.media && Array.isArray(mediaItem.relationships.media)) {
          const mediaWithType = mediaItem.relationships.media.map((media: any) => ({
            ...media,
            _entityType: "media" // Media entities
          }));
          allCurrentRels.push(...mediaWithType);
        }
      }

      console.log("[EntityForm] Found ALL current relationships:", allCurrentRels);
      console.log("[EntityForm] Total relationship count:", allCurrentRels.length);

      // Debug: Show what we're filtering for
      console.log("[EntityForm] Filtering for:");
      console.log("[EntityForm] - Current item ID:", currentItem?.id);
      console.log("[EntityForm] - Current item name:", currentItem?.name);
      console.log("[EntityForm] - Endpoint tableName:", endpoint?.tableName);
      console.log("[EntityForm] - Endpoint relationshipKey:", endpoint?.relationshipKey);

      // Filter out the relationship to be removed (the current item)
      // We need to match both ID and entity type to be truly correct
      const updatedRels = allCurrentRels.filter((relationship) => {
        const isSameId = relationship.id === currentItem?.id;

        // Check if the entity type matches the current endpoint
        const isSameEntityType =
          relationship._entityType === endpoint?.relationshipKey ||
          relationship?._entityType === endpoint?.tableName ||
          relationship._entityType === endpoint?.tableName ||
          // Handle special case for media entities
          (relationship._entityType === "media" && endpoint?.tableName === "media");

        const willBeRemoved = isSameId && isSameEntityType;

        // Debug: Log each relationship's decision
        console.log("[EntityForm] Relationship:", {
          name: relationship.entity_name || relationship.name,
          id: relationship.id,
          entityType: relationship._entityType,
          isSameId,
          isSameEntityType,
          willBeRemoved
        });

        // Only remove if both ID and entity type match (most precise filtering)
        return !willBeRemoved;
      });

      console.log("[EntityForm] Updated relationships after filtering out current item:", updatedRels);
      console.log("[EntityForm] Relationships remaining after unlink:", updatedRels.length);

      setDialogRelationships({
        current: allCurrentRels,
        updated: updatedRels
      });

      // Store the complete media item for sending the full relationship update
      setMediaItemForUnlink(mediaItem);
      setUnlinkDialogOpen(true);
    } catch (error) {
      console.error("Error preparing unlink:", error);

      // Handle specific error cases
      if (error instanceof Error && error.message && error.message.includes("404")) {
        enqueueSnackbar("Media item no longer exists (may have been deleted)", {
          variant: "warning"
        });

        // Remove from local state since it's already deleted
        const deletedMediaId = parseInt(selectedMediaItem?.id?.toString() || "0");
        setRelatedMedia((prevMedia) => prevMedia.filter((media) => media?.id?.toString() !== deletedMediaId));

        // Clear selection
        setSelectedMediaItem(null);
      } else {
        enqueueSnackbar("Failed to prepare unlink operation", {
          variant: "error"
        });
      }
    }
  };

  // Resolves the v2 relationship_id for a media item linked to this CM table row.
  // Tries the stored value first; if missing, fetches the media detail and searches
  // its related_entities for the entry whose id matches the current entity.
  const resolveMediaRelationshipId = async (mediaId: number): Promise<number | undefined> => {
    const stored = relatedMedia.find((m: any) => Number(m.id) === mediaId);
    if ((stored as any)?.relationship_id) return (stored as any).relationship_id;
    try {
      const detail = await apiFetch(`${REACT_APP_API_ROOT}api/v2/media/${mediaId}/?include_relations=true`);
      const normalised = normaliseRelations(detail?.relationships ?? {});
      const allRelated: any[] = Object.values(normalised.byTableSlug).flat() as any[];
      const match = allRelated.find((r: any) => String(r.id) === String(currentItem?.id));
      return match?.relationship_id;
    } catch {
      return undefined;
    }
  };

  // Handle unlink media
  const handleUnlinkMedia = async () => {
    if (!selectedMediaItem || !endpoint || !dialogRelationships || !mediaItemForUnlink) return;

    // CM-Table rows use v2 UnifiedRelationship — delete the relationship record directly
    if (isCmTableEndpoint(endpoint)) {
      const mediaId = parseInt(selectedMediaItem.id?.toString() ?? "");
      const relationshipId = (selectedMediaItem as RelatedEntityItem).relationship_id ?? (await resolveMediaRelationshipId(mediaId));
      if (!relationshipId) {
        enqueueSnackbar("Cannot unlink: missing relationship ID", { variant: "error" });
        return;
      }
      try {
        await deleteCmTableMediaRelationship(relationshipId);
        setUnlinkDialogOpen(false);
        const unlinkedMediaId = parseInt(selectedMediaItem.id?.toString() ?? "");
        const filteredMedia = (currentItem?.relationships as any)?.media?.filter((m: any) => m.id !== unlinkedMediaId) ?? [];
        const updatedRelationships = { ...((currentItem?.relationships ?? {}) as any), media: filteredMedia };

        if (currentItem?.relationships?.media) {
          setCurrentItem({ ...currentItem, relationships: updatedRelationships });
        } else {
          setRelatedMedia((prev) => prev.filter((m) => m.id !== unlinkedMediaId));
        }

        // useSelectedEntity's CM Table fast-path reads relationships from the
        // cm-table-row cache (the ["entity",...] L2/L3 queries are disabled for CM
        // Table endpoints), so that's the entry the Find Related drawer and media
        // strip actually subscribe to — patch it directly so they update synchronously.
        const tableSlug = getCmTableSlugFromEndpoint(endpoint) || endpoint.tableName;
        queryClient.setQueryData(getCmTableRowQueryKey(tableSlug, currentItem?.id), (old: any) =>
          old ? { ...old, relationships: updatedRelationships } : old
        );
        setSelectedMediaItem(null);
        setHasLocalMediaChanges(true);
        enqueueSnackbar("Media unlinked successfully", { variant: "success" });
      } catch (err: any) {
        enqueueSnackbar(err?.message || "Failed to unlink media", { variant: "error" });
      }
      return;
    }

    // Spatial datasets use the same v2 relationship DELETE endpoint as CM tables.
    if (endpoint?.entityType === "spatial-dataset") {
      const mediaId = parseInt(selectedMediaItem.id?.toString() ?? "");
      const relationshipId = (selectedMediaItem as RelatedEntityItem).relationship_id ?? (await resolveMediaRelationshipId(mediaId));
      if (!relationshipId) {
        enqueueSnackbar("Cannot unlink: missing relationship ID", { variant: "error" });
        return;
      }
      try {
        await deleteCmTableMediaRelationship(relationshipId);
        setUnlinkDialogOpen(false);
        const unlinkedMediaId = parseInt(selectedMediaItem.id?.toString() ?? "");
        // Update local relatedMedia state
        setRelatedMedia((prev) => prev.filter((m) => m.id !== unlinkedMediaId));
        setSelectedMediaItem(null);
        setHasLocalMediaChanges(true);
        enqueueSnackbar("Media unlinked successfully", { variant: "success" });
      } catch (err: any) {
        enqueueSnackbar(err?.message || "Failed to unlink media", { variant: "error" });
      }
      return;
    }

    try {
      // Create the complete updated relationships by copying all existing relationships
      // and removing only the specific relationship we're unlinking
      const updatedRelationships = {
        media: [],
        related_entities: {},
        geo_entities: {},
        ...mediaItemForUnlink.relationships
      };

      // Update the specific relationship type to remove the current item
      const relationshipKey = endpoint.relationshipKey!;
      const relationshipSearchingKey = endpoint?.tableName ?? "";
      let relationshipGroupName: "related_entities" | "geo_entities" = "related_entities";
      // find the correct relationship group, so we can update correct relationship
      if (updatedRelationships) {
        if (updatedRelationships?.geo_entities?.[relationshipSearchingKey]) {
          relationshipGroupName = "geo_entities";
        }
      }

      // build relationship list
      if (updatedRelationships && updatedRelationships?.[relationshipGroupName]?.[relationshipSearchingKey]) {
        delete updatedRelationships[relationshipGroupName][relationshipSearchingKey];
        updatedRelationships[relationshipGroupName][relationshipKey] = dialogRelationships.updated
          .filter((rel) => {
            return relationshipKey === rel?._entityType;
          })
          ?.map((rel: RelatedEntityItem & { _entityType?: string; relationship_id?: number; thumbnail?: string }) => ({
            id: rel.id,
            name: rel?.name || rel.entity_name,
            relationship_id: rel?.relationship_id,
            entity_description: rel?.description,
            rel_description: rel?.rel_description,
            thumbnail: rel?.thumbnail || rel?.entity_thumbnail
          }));
      }

      // Create form data using the correct backend format
      const formData = new FormData();
      formData.append("relationships", JSON.stringify(updatedRelationships));
      entityMediaUnlinkMutation({ id: selectedMediaItem?.id, data: formData });
    } catch (error: any) {
      console.error("Error unlinking media:", error);
      enqueueSnackbar(error?.message || "Failed to unlink media", { variant: "error" });
    }
  };

  // Prepare delete media
  const prepareDelete = async (mediaId: number | string) => {
    if (mode === "create") {
      const item = relatedMedia?.find((media) => media.id?.toString() === mediaId?.toString());
      if (item) {
        setUploadMediaList((prev) => {
          return prev?.filter((media) => !(media.name === item?.name && media.type === item.type));
        });

        const filteredRelatedMedia = relatedMedia?.filter((media) => !(media.name === item?.name && media.type === item.type)) ?? [];
        const nextRelatedMedia =
          filteredRelatedMedia.length === 1
            ? filteredRelatedMedia.map((media, index) => (index === 0 ? { ...media, is_primary: true } : { ...media }))
            : filteredRelatedMedia.map((media) => ({ ...media }));

        setRelatedMedia(nextRelatedMedia);
        setSelectedMediaItem(uploadMediaList?.length === 1 ? { ...relatedMedia?.[0], is_primary: true } : null);
      }
      return;
    }

    try {
      // check whether file is local,
      // if file is local, delete from local state and update state
      const media = relatedMedia?.find((media) => media?.id === mediaId);
      if (media?.file) {
        const excludeMedia = relatedMedia.filter((item) => item?.id !== media?.id);
        setRelatedMedia(excludeMedia);

        // clear selected media if id is same as deleting media id
        if (selectedMediaItem?.id?.toString() === mediaId?.toString()) {
          setSelectedMediaItem(null);
        }

        // clear primary media id if is same as deleting media id
        if (selectedMediaItem?.id?.toString() === mediaId?.toString()) {
          setSelectedMediaItem(null);
        }
        return;
      }

      console.log("[EntityForm] prepareDelete called for media ID:", mediaId);

      // Find the media endpoint from config
      const mediaEndpoint = config.apiEndpoints.find((endpoint) => endpoint.tableName === "media");
      if (!mediaEndpoint) {
        throw new Error("Media endpoint not found in configuration");
      }

      // Fetch the media item with relationships using the generalized system
      const mediaItem = (await fetchItemWithRelationships(mediaId, mediaEndpoint, profile)) as MediaItem;

      console.log("[EntityForm] Media item fetched for delete:", mediaItem);
      console.log("[EntityForm] Media item relationships:", mediaItem.relationships);

      // Log the relationships structure for debugging
      if (mediaItem.relationships) {
        console.log("[EntityForm] Related entities:", mediaItem.relationships.related_entities);
        console.log("[EntityForm] Geo entities:", mediaItem.relationships.geo_entities);
        console.log("[EntityForm] Media relationships:", mediaItem.relationships.media);
      }

      setDeleteMediaItem(mediaItem);
      setDeleteDialogOpen(true);
    } catch (error) {
      console.error("Error preparing delete:", error);

      // Handle specific error cases
      if (error instanceof Error && error.message && (error.message.includes("404") || error?.message?.includes("not found"))) {
        enqueueSnackbar("Media item no longer exists (may have been deleted)", {
          variant: "warning"
        });

        // Remove from local state since it's already deleted
        const deletedMediaId = parseInt(selectedMediaItem?.id?.toString() || "0");
        setRelatedMedia((prevMedia) => prevMedia.filter((media) => media?.id?.toString() !== deletedMediaId));

        // Clear selection
        setSelectedMediaItem(null);
      } else {
        enqueueSnackbar("Failed to prepare delete operation", {
          variant: "error"
        });
      }
    }
  };

  // If the media that was just deleted is currently being previewed in the
  // Layer B media detail overlay (MediaEntityDetailModal), close that overlay
  // so the user isn't left looking at a now-nonexistent media item.
  const closeMediaPreviewIfDeleted = (deletedMediaId: number | string | undefined) => {
    if (deletedMediaId === undefined || deletedMediaId === null) return;
    const { isMediaDetailOpen, selectedItem, actions } = useMediaStore.getState();
    if (isMediaDetailOpen && selectedItem?.id?.toString() === deletedMediaId.toString()) {
      actions.closeMediaDetail();
    }
  };

  // Handle delete media
  const handleDeleteMediaItem = async () => {
    if (!selectedMediaItem) return;

    // CM-Table rows: permanently delete the media item via v2 media DELETE endpoint.
    // The relationship is removed automatically by the backend when the media is deleted.
    if (isCmTableEndpoint(endpoint)) {
      const mediaId = parseInt(selectedMediaItem.id?.toString() ?? "");
      try {
        await deleteV2MediaItem(mediaId);
        setDeleteDialogOpen(false);
        const deletedMediaId = parseInt(selectedMediaItem.id?.toString() ?? "");
        const filteredMedia = (currentItem?.relationships as any)?.media?.filter((m: any) => m.id !== deletedMediaId) ?? [];
        const updatedRelationships = { ...((currentItem?.relationships ?? {}) as any), media: filteredMedia };

        if (currentItem?.relationships?.media) {
          setCurrentItem({ ...currentItem, relationships: updatedRelationships });
        } else {
          setRelatedMedia((prev) => prev.filter((m) => m.id !== deletedMediaId));
        }

        // useSelectedEntity's CM Table fast-path reads relationships from the
        // cm-table-row cache (the ["entity",...] L2/L3 queries are disabled for CM
        // Table endpoints), so that's the entry the Find Related drawer and media
        // strip actually subscribe to — patch it directly so they update synchronously.
        const tableSlug = getCmTableSlugFromEndpoint(endpoint) || endpoint?.tableName;
        queryClient.setQueryData(getCmTableRowQueryKey(tableSlug, currentItem?.id), (old: any) =>
          old ? { ...old, relationships: updatedRelationships } : old
        );
        setSelectedMediaItem(null);
        setHasLocalMediaChanges(true);

        // Close the Layer B media preview if the deleted item was open, and
        // invalidate the drawer caches so the Find Related drawer and bottom
        // media drawer drop the deleted media (mirrors the upload refresh path).
        closeMediaPreviewIfDeleted(deletedMediaId);
        invalidateDataDrawerCache();

        enqueueSnackbar("Media removed successfully", { variant: "success" });
      } catch (err: any) {
        enqueueSnackbar(err?.message || "Failed to remove media", { variant: "error" });
      }
      return;
    }

    try {
      const id = selectedMediaItem?.id;
      entityMediaDeleteMutation({ id });
    } catch (error) {
      console.error("Error deleting media:", error);
      enqueueSnackbar("Failed to delete media", { variant: "error" });
    }
  };

  function updatePrimaryMedia(media: MediaItem[], id: string) {
    return media.map((item) => ({ ...item, is_primary: item?.id?.toString() === id }));
  }

  function validateUploadFileType(file: File) {
    if (!file) return false;

    const fileType = file.type;
    if (!fileType?.startsWith("image/")) {
      enqueueSnackbar("Only image type is allowed to upload.", { variant: "error" });
      return false;
    }
    return true;
  }

  async function handleNewMediaSelect(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);

    if (files && files?.length > 0) {
      if (files?.length > 1) {
        enqueueSnackbar("Multiple image is not allowed to upload at once", { variant: "error" });
        return;
      }

      const file = files?.[0];
      const isValidFileType = validateUploadFileType(file);
      if (!isValidFileType) return;

      const fileSizeInKB = Math.round(file.size / 1024);
      // file size must be at least 1KB
      if (fileSizeInKB < 1) {
        enqueueSnackbar(`File "${file.name}" is too small. Please upload a file larger than 1 KB`, { variant: "error" });
        return;
      }

      const isValidFileSize = isFileSizeValid(file);
      if (!isValidFileSize) {
        enqueueSnackbar(`File "${file.name}" exceeds maximum size limit of 4GB`, { variant: "error" });
        return;
      }

      // check for duplicate file
      if (uploadMediaList?.length && mode === "create") {
        const isExist = uploadMediaList?.some((fs) => fs.name === file.name && fs.size === file.size);
        if (isExist) {
          enqueueSnackbar(`File "${file.name}" already added to upload queue`, { variant: "warning" });
          return;
        }
      }

      if (mode === "edit") {
        if (files?.length > 1) {
          enqueueSnackbar("Multiple image is not allowed to upload at once", { variant: "error" });
          return;
        }

        if (currentItem) {
          await handleMediaUpload(files, currentItem);
          return;
        }
      } else {
        setUploadMediaList((prev) => files.concat(prev || []));

        const media = files?.map((file) => {
          const fileUrl = URL.createObjectURL(file);
          return {
            id: generateUUID(),
            name: file.name,
            size: file.size,
            entity_thumbnail: fileUrl,
            media_type: file.type,
            type: file.type
          };
        });

        setRelatedMedia((prev) => {
          const mediaList = media.concat(prev);
          return mediaList?.map((item, idx) => {
            // set new primary image
            return { ...item, is_primary: idx === 0 };
          });
        });
      }
    }

    // rest file value
    event.target.value = "";
  }

  // upload new image and set uploaded image as primary
  async function handleMediaUpload(files: File[], entity: EntityItem | MediaItem) {
    try {
      if (!files || !files?.length) return;

      const uploadFilesDetail = files?.map(prepareUploadFileDetail);
      setUploadMediaDetails(uploadFilesDetail as any);

      // upload file details for progress tracker
      setShowUploadProgressModal(true);

      // meta data for upload progress
      let metadata: any[] = [];

      // meta data for bulk upload
      if (mode === "create") {
        const isCmTableCreate = isCmTableEndpoint(endpoint);
        const transformedRelationships = isCmTableCreate ? {} : transformRelationships(entity?.relationships as any, config);
        const primaryMedia = relatedMedia?.find((media) => media?.is_primary);
        const hasPrimaryMedia = !!primaryMedia;

        metadata =
          uploadFilesDetail?.map((item) => {
            const isPrimaryImage =
              hasPrimaryMedia && item.original_file?.filename == primaryMedia.name && item?.original_file?.type === primaryMedia.type;

            const meta: any = {
              id: item.id,
              name: item.name || "",
              description: ""
            };

            // CM tables: relationships handled separately via v2 API after upload
            if (!isCmTableCreate) {
              meta.relationships = {
                ...transformedRelationships,
                related_entities: {
                  [selectedEndpoint?.relationshipKey as string]: [{ id: entity.id, is_primary: isPrimaryImage }]
                }
              };
            }

            return meta;
          }) || [];
      } else {
        // meta data for single upload on `Edit` mode
        // CM tables use a separate relationship API instead of embedding relationships in the payload
        const isCmTable = isCmTableEndpoint(endpoint);
        metadata = [
          {
            id: uploadFilesDetail?.[0]?.id,
            name: uploadFilesDetail?.[0]?.name,
            description: "",
            ...(isCmTable
              ? {}
              : {
                  relationships: {
                    related_entities: {
                      [selectedEndpoint?.relationshipKey as string]: [{ id: entity?.id, is_primary: true }]
                    }
                  }
                })
          }
        ];
      }

      // Extract EXIF / GPS from each file concurrently and merge into metadata
      const exifResults = await Promise.all(
        files.map((f) => extractExifFromFile(f).catch(() => ({ exif_metadata: null, geometry: null })))
      );
      metadata = metadata.map((m, i) => ({
        ...m,
        exif_metadata: exifResults[i]?.exif_metadata ?? null,
        geometry: exifResults[i]?.geometry ?? null
      }));

      // initiate upload file
      const uploadReq = await uploadFiles(files, metadata);

      // For CM tables in create mode: create v2 relationships for each uploaded file in parallel
      if (mode === "create" && isCmTableEndpoint(endpoint)) {
        const primaryMedia = relatedMedia?.find((media) => media?.is_primary);
        await Promise.all(
          uploadReq.map(async (result, i) => {
            const mediaId = parseInt(String(result.remoteDetails?.id));
            if (!Number.isFinite(mediaId)) return;
            const file = result.file;
            // Exactly one upload should be primary: the one matching the user's
            // "Set as Primary" selection, or the first file if none was selected.
            const isPrimary = primaryMedia ? file.name === primaryMedia.name && file.type === (primaryMedia as any).type : i === 0;
            await createCmTableMediaRelationship(entity.id, mediaId, isPrimary);
            if (isPrimary) {
              await setCmTablePrimaryImage(mediaId, entity.id);
            }
          })
        );
      }

      if (mode === "edit") {
        // set primary image manually
        const uploadRes = uploadReq?.[0];
        const uploadItemId = uploadRes.remoteDetails?.id;
        const mediaId = parseInt(String(uploadItemId));

        // For CM tables, create the relationship and set primary image via the v2 relationships API
        if (isCmTableEndpoint(endpoint)) {
          if (!Number.isFinite(mediaId)) {
            console.warn("[EntityForm] Skipping CM Table relationship create — invalid mediaId:", uploadItemId);
          } else {
            // Only set as primary when no existing images
            const isPrimary = relatedMedia.length === 0;
            await createCmTableMediaRelationship(entity.id, mediaId, isPrimary);
            if (isPrimary) {
              await setCmTablePrimaryImage(mediaId, entity.id);
            }
          }

          // Re-fetch the row so currentItem is in sync — the currentItem useEffect
          // picks this up and calls setRelatedMedia, so we must not call it again below
          const tableSlug = getCmTableSlugFromEndpoint(endpoint);
          if (tableSlug) {
            const updatedRowRes = await getCmTableRow(tableSlug, entity.id, { include_relations: true });
            setCurrentItem(updatedRowRes.data as any);
            // Invalidate geo cache (popup thumbnail) and data cache (sidebar grid thumbnail)
            queryClient.invalidateQueries({ queryKey: ["cm-table-geo", tableSlug], exact: false });
            queryClient.invalidateQueries({ queryKey: ["cm-table-data", tableSlug], exact: false });
          }
        } else if (endpoint?.entityType === "spatial-dataset") {
          // For spatial datasets, create the relationship via POST /api/v2/relationships/
          // with from_type: "spatialdataset". Set primary image immediately when no other media exists.
          if (Number.isFinite(mediaId)) {
            const isPrimary = relatedMedia.length === 0;
            await createSpatialDatasetMediaRelationship(entity.id, mediaId, isPrimary);
            if (isPrimary) {
              await setSpatialDatasetPrimaryImage(mediaId, entity.id);
            }
          }
        }

        // fetch newly uploaded media details and update local media list
        const mediaRes = await apiFetch(`${REACT_APP_API_ROOT}api/v2/media/${uploadItemId}/`);
        // make newly upload details compatible for related media type
        const finalThumbnailUrl = getRelationshipMediaThumbnailUrl(mediaRes);
        const finalMediaItem = {
          id: mediaRes.id,
          entity_name: mediaRes?.name,
          name: mediaRes?.name,
          entity_description: mediaRes?.description,
          entity_thumbnail: finalThumbnailUrl || mediaRes?.thumbnail
        };

        // Mark this media as newly uploaded so it gets retry logic
        setNewlyUploadedMediaIds((prev) => new Set(prev).add(mediaRes.id));

        // For non-CM tables (including spatial datasets): manually prepend the new item as primary.
        // CM tables rely on setCurrentItem above — the currentItem useEffect calls
        // setRelatedMedia from the fresh server data, so calling it again here
        // would add the item a second time (duplicate).
        if (!isCmTableEndpoint(endpoint)) {
          setRelatedMedia((prev) => {
            const removeOldPrimaryTrack = prev.map((item) => {
              const updatedItem = { ...item };
              if (item.is_primary) {
                delete updatedItem.is_primary;
              }
              return updatedItem;
            });
            return [{ ...finalMediaItem, is_primary: true }, ...removeOldPrimaryTrack];
          });
        }

        // set to latest thumbnail track ref
        latestPrimaryThumbnailRef.current = finalThumbnailUrl;
      }

      // @TODO: sync popup with latest changed thumbnail
      // update map popup with latest thumbnail
      // if (shouldUpdateLocalPrimaryImageList) {
      //   setCurrentItem((prev: any) => ({ ...prev, thumbnail: finalThumbnailUrl }));
      // }
      //

      setIsUploadFinish(true);

      setSelectedMediaItem(null);

      // invalidate data drawer list
      invalidateDataDrawerCache();

      // refresh options to avoid stale relationship id validation check
      await refreshOptions();

      // In create mode, auto-close the progress modal so the user doesn't have to click done or add a new button.
      if (mode === "create") {
        setTimeout(() => handleUploadAcknowledge(entity), 1000);
      }
    } catch (error: any) {
      console.log("[EntityForm]: upload media error: ", error?.message);
      enqueueSnackbar(error?.message || "Failed to upload media", { variant: "error" });

      setIsUploadFailed(true);

      // close progress modal on failures
      setShowUploadProgressModal(false);
    }
  }

  function handleUploadAcknowledge(entityOverride?: any) {
    setUploadMediaDetails(undefined);
    setShowUploadProgressModal(false);

    // reset upload finish status
    setIsUploadFinish(false);

    if (mode === "create") {
      // entityOverride is supplied by the auto-close setTimeout to avoid the stale-closure problem where currentItem is still null at that point.stale-closure problem where currentItem is still null at that point.
      onItemCreated?.(entityOverride ?? currentItem);
      onClose();
      resetForm();
    }
  }

  function handleUploadFailed() {
    handleUploadAcknowledge();
    setIsUploadFailed(false);
  }

  // cleanup state
  useEffect(() => {
    if (!open) {
      setRelatedMedia([]);
      setUploadMediaList(null);
      setSelectedMediaItem(null);
      setNewlyUploadedMediaIds(new Set());
      setUploadMediaDetails(undefined);
    }
  }, [open]);

  // revoke newly selected file url on form close
  useEffect(() => {
    if (!open && relatedMedia?.length) {
      relatedMedia?.forEach((media) => {
        if (media.file) {
          URL.revokeObjectURL(media.entity_thumbnail || media?.url || media.entity_url);
        }
      });
    }
  }, [relatedMedia, open]);

  if (!endpoint) {
    return null;
  }

  const entityName = currentItem?.name || existingItem?.name || endpoint.name.slice(0, -1);

  const endpointName = endpoint?.name;
  // make singular is name plural, if not use as it is
  const endpointTitleName = endpointName?.toLowerCase()?.endsWith("s") ? endpoint?.name?.slice(0, -1) : endpointName;
  const dialogTitle = mode === "edit" ? `Editing ${entityName || ""}` : `Create New ${endpointTitleName}`;

  const shouldShowMap = isGeoEntity && !isEntityTypeMedia;

  return (
    <Dialog
      open={open}
      onClose={!isSubmitting ? onClose : undefined}
      maxWidth={isGeoEntity ? "xl" : showEditRelationships ? "xl" : "sm"}
      fullWidth
      scroll='paper'
      PaperProps={{
        sx: {
          borderTop: "5px solid",
          borderColor: "primary.main",
          borderTopRightRadius: 0,
          borderTopLeftRadius: 0,
          transition: "max-width 350ms ease",
          overflow: "hidden",
          "& *": {
            boxSizing: "border-box"
          }
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Grid container sx={{ width: "100%", maxWidth: "100%", maxHeight: "calc(100dvh - 10rem)", overflow: "hidden" }}>
          <Grid
            item
            ref={setFormColumnEl}
            sm={isGeoEntity ? 7 : 12}
            sx={{
              minWidth: 0,
              maxWidth: isGeoEntity ? "50%" : "100%",
              borderRight: "2px solid #F7F7F7",
              padding: "1.5rem",
              pr: "0",
              // Positioned so a blocked save can scroll the first form error into view.
              position: "relative",
              // Scroll the form column independently when it has many fields
              // (e.g. form-schema capture inputs) instead of clipping them.
              maxHeight: "calc(100dvh - 10rem)",
              overflowY: "auto"
            }}
          >
            {/* Header with AppBar */}
            <Box position='static' sx={{ background: "transparent", color: "grey.900" }}>
              <Typography variant='h3' component='h1' fontWeight={"500"}>
                {dialogTitle}
              </Typography>
            </Box>
            <Divider sx={{ borderColor: "#F4F4F4", my: "1rem" }} />

            {/* Form Content */}
            <Stack
              spacing={"1rem"}
              sx={{
                overflowY: "auto",
                height: "100%",
                maxHeight: "calc(100dvh - 15.3rem)",
                pr: "1.5rem",
                paddingBottom: "1rem"
              }}
              className='scroller'
            >
              {/* The record's own name/description columns, for CM tables with no
                  bound form. A form-bound row derives both from the fields its
                  author picked (Record Title / Record Description), so neither is
                  typed here — see the form block below. */}
              {!formSuppliesName && !awaitingFormSchema && (
                <FormControl sx={{ gap: "0.37rem" }}>
                  <Typography
                    htmlFor='name'
                    variant='body1'
                    component='label'
                    sx={{ position: "unset", transform: "none", color: "grey.600" }}
                  >
                    Name
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    autoFocus
                    name='name'
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    placeholder='Name of the place, site, or record'
                    inputProps={{ maxLength: 100 }}
                    helperText={`${formData.name.length}/100`}
                    sx={{
                      "& > input": {
                        padding: "0.94rem 0.875rem",
                        height: "auto"
                      },
                      "& fieldset": {
                        borderColor: "grey.300",
                        borderRadius: "0.5rem"
                      },
                      "& .MuiFormHelperText-root": {
                        textAlign: "right",
                        color: formData.name.length >= 100 ? "error.main" : "text.secondary"
                      }
                    }}
                  />
                </FormControl>
              )}

              {showGenericDescription && (
                <FormControl sx={{ gap: "0.37rem" }}>
                  <Typography
                    htmlFor='description'
                    variant='body1'
                    component='label'
                    sx={{ position: "unset", transform: "none", color: "grey.600" }}
                  >
                    Description
                  </Typography>
                  <TextField
                    name='description'
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder='Briefly describe this place and its significance'
                    fullWidth
                    multiline
                    rows={4}
                    disabled={isSubmitting}
                    inputProps={{ maxLength: 250 }}
                    helperText={`${formData.description.length}/250`}
                    sx={{
                      "& > textarea": {
                        padding: "0.94rem 0.875rem"
                      },
                      "& fieldset": {
                        borderColor: "grey.300",
                        borderRadius: "0.5rem"
                      },
                      "& .MuiFormHelperText-root": {
                        textAlign: "right",
                        color: formData.description.length >= 250 ? "error.main" : "text.secondary"
                      }
                    }}
                  />
                </FormControl>
              )}

              {/* The bound form's own fields — rendered inline, in author order,
                  right where the record starts. Media, record links, geometry and
                  the map stay below in EntityForm's own sections. */}
              {endpoint && isCmTableEndpoint(endpoint) && (
                <>
                  {formHeading && (
                    <Stack direction='row' alignItems='baseline' spacing={"0.5rem"} sx={{ pt: "0.25rem" }}>
                      <Typography variant='body1' color='grey.800' fontWeight={600}>
                        {formHeading}
                      </Typography>
                      {resolvedForm.activeForm && (
                        <Typography variant='caption' color='grey.500'>
                          v{resolvedForm.activeForm.version}
                        </Typography>
                      )}
                    </Stack>
                  )}
                  <CmFormCaptureFields
                    resolved={resolvedForm}
                    values={captureValues}
                    onChange={handleCaptureChange}
                    disabled={isSubmitting}
                    mode={mode}
                    showErrors={showCaptureErrors}
                    onValidityChange={handleCaptureValidityChange}
                    expectsForm={Boolean(endpoint.form?.uuid)}
                  />
                  {/* What the record will be called and described as, so a value
                      built from other fields is never a surprise after saving. */}
                  {(titleIsDerived || descriptionIsDerived) && (
                    <Stack spacing={"0.12rem"}>
                      {titleIsDerived && (
                        <Typography variant='caption' color='grey.500'>
                          Record title:{" "}
                          {formData.name.trim() ? (
                            <Box component='span' sx={{ color: "grey.800", fontWeight: 500 }}>
                              {formData.name.trim()}
                            </Box>
                          ) : (
                            `built from ${describeTitleSource(activeSchema)}`
                          )}
                        </Typography>
                      )}
                      {descriptionIsDerived && (
                        <Typography variant='caption' color='grey.500'>
                          Record description:{" "}
                          {formData.description.trim() ? (
                            <Box component='span' sx={{ color: "grey.800", fontWeight: 500 }}>
                              {formData.description.trim()}
                            </Box>
                          ) : (
                            `built from ${describeDescriptionSource(activeSchema)}`
                          )}
                        </Typography>
                      )}
                    </Stack>
                  )}
                </>
              )}

              {/* CM-table schema-driven fields (row_data), incl. the icon/color styling selector */}
              {endpoint && isCmTableEndpoint(endpoint) && (
                <CmTableRowDataFields
                  endpoint={endpoint as ApiEndpoint}
                  mapDisplayConfig={mapDisplayConfig}
                  onMapDisplayConfigChange={setMapDisplayConfig}
                  disabled={isSubmitting}
                />
              )}

              {/* Relationships */}
              {mode === "edit" && currentItem && (
                <Box>
                  <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
                    <Typography variant='body1' color='grey.600'>
                      Relationships
                    </Typography>
                    <Button
                      endIcon={<LinkIcon fontSize='small' />}
                      onClick={handleEditRelationships}
                      size='small'
                      sx={{ color: "grey.500", textTransform: "none", fontSize: "0.875rem" }}
                    >
                      Edit
                    </Button>
                  </Stack>
                  <Paper
                    variant='outlined'
                    sx={{ maxHeight: "14em", minHeight: 60, p: "0.94rem 0.875rem", borderRadius: "0.5rem", overflowY: "auto" }}
                    className='scroller'
                  >
                    <RelationshipChips relationships={displayRelationships} config={config} />
                  </Paper>
                </Box>
              )}

              {/* Restrictions — only for users who can manage them */}
              {showRestrictionsField && (
                <Box>
                  <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
                    <Typography variant='body1' color='grey.600'>
                      Restrictions
                    </Typography>
                    <Button
                      endIcon={<LockOutlined fontSize='small' />}
                      onClick={() => setShowRestrictionsPicker(true)}
                      disabled={isSubmitting}
                      size='small'
                      sx={{ color: "grey.500", textTransform: "none", fontSize: "0.875rem" }}
                    >
                      Edit
                    </Button>
                  </Stack>
                  <Paper
                    variant='outlined'
                    sx={{ maxHeight: "14em", minHeight: 60, p: "0.94rem 0.875rem", borderRadius: "0.5rem", overflowY: "auto" }}
                    className='scroller'
                  >
                    {selectedRestrictionIds.length === 0 ? (
                      <Typography variant='body1' color='grey.400'>
                        No restrictions applied
                      </Typography>
                    ) : (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selectedRestrictionIds.map((id) => (
                          <CMChip
                            key={id}
                            label={restrictionNameById.get(id) ?? `ID: ${id}`}
                            size='small'
                            sx={{
                              height: "2.125rem",
                              bgcolor: "grey.200",
                              color: "grey.800",
                              padding: "0.5rem 0.75rem",
                              borderRadius: "0.5rem",
                              boxShadow: "none",
                              "& .MuiChip-label": { fontSize: "0.875rem", px: 0.75, py: 0 }
                            }}
                          />
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}

              {/* Media */}
              {(() => {
                // Performance logs reduced for cleaner output - major optimizations already applied
                // Original detailed logging removed to reduce noise from duplicate renders
                return null;
              })()}

              {/* Don't show media upload option for media entity and endpoint */}
              {!isEntityTypeMedia && (
                <Box>
                  <Stack direction='row' justifyContent='space-between' alignItems='center' mb={"0.5rem"}>
                    <Typography variant='body1' color='grey.600'>
                      Media ({relatedMedia.length} items)
                    </Typography>
                    <Stack direction='row' spacing={"1rem"}>
                      <Button variant='outlined' color='primary' sx={{ overflow: "hidden", position: "relative" }}>
                        Upload Image
                        <input
                          type='file'
                          accept='image/*'
                          multiple={false}
                          aria-hidden={true}
                          style={{ position: "absolute", opacity: 0, height: "100%", width: "100%", cursor: "pointer" }}
                          onChange={handleNewMediaSelect}
                        />
                      </Button>
                      {selectedMediaItem && (
                        <>
                          <Button
                            variant='outlined'
                            onClick={handleSetPrimaryImage}
                            sx={{ color: "grey.500", borderColor: "grey.500" }}
                            disabled={selectedMediaItem?.is_primary}
                          >
                            Set as Primary
                          </Button>
                          {mode !== "create" && (
                            <Button
                              onClick={() => prepareUnlink(parseInt(selectedMediaItem?.id?.toString()))}
                              color='warning'
                              startIcon={<LinkIcon />}
                              aria-label='Unlink media relationships'
                            >
                              Unlink
                            </Button>
                          )}

                          {canDelete && (
                            <IconButton
                              onClick={() => prepareDelete(selectedMediaItem?.id)}
                              color='error'
                              aria-label='Delete'
                              title='Delete media permanently'
                            >
                              <DeleteIcon aria-hidden='true' />
                            </IconButton>
                          )}
                        </>
                      )}
                    </Stack>
                  </Stack>
                  <Paper
                    variant='outlined'
                    sx={{
                      minHeight: "190px", // Reduced from 250px - just enough for carousel + padding
                      padding: "0.75rem",
                      bgcolor: "grey.50",
                      borderRadius: "0.5rem"
                    }}
                  >
                    {(() => {
                      console.log("[EntityForm] About to render ImageCarousel with:", {
                        mediaItemsLength: relatedMedia.slice(0, displayedMediaCount).length,
                        selectedPrimaryImage: relatedMedia?.find((media) => media?.is_primary)?.id,
                        displayedMediaCount,
                        mediaItems: relatedMedia.slice(0, displayedMediaCount).map((item) => ({
                          id: item.id,
                          name: item.entity_name || item.name,
                          entity_thumbnail: item.entity_thumbnail,
                          hasThumbnail: !!item.entity_thumbnail
                        }))
                      });
                      return null;
                    })()}
                    <MediaCarouselProvider
                      selectedItemId={selectedMediaItem ? parseInt(selectedMediaItem?.id?.toString()) : undefined}
                      onItemSelect={handleSelectMedia}
                    >
                      <MediaCarousel>
                        {relatedMedia?.slice(0, displayedMediaCount)?.map((item) => {
                          return (
                            <MediaItemWithRetry
                              key={item?.id}
                              item={item}
                              isNewlyUploaded={newlyUploadedMediaIds.has(item.id)}
                              onSuccess={() => {
                                // Remove from newly uploaded set when thumbnail loads
                                setNewlyUploadedMediaIds((prev) => {
                                  const next = new Set(prev);
                                  next.delete(item.id);
                                  return next;
                                });
                              }}
                            />
                          );
                        })}
                      </MediaCarousel>
                    </MediaCarouselProvider>

                    {displayedMediaCount < relatedMedia.length && (
                      <Box textAlign='center' mt={2}>
                        <Button variant='text' onClick={handleLoadMoreMedia} size='small'>
                          Load More ({relatedMedia.length - displayedMediaCount} remaining)
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}

              {/* Geometry (Location/Area/Route) */}
              {isGeoEntity && (
                <>
                  <Box>
                    <Typography variant='body1' color='grey.500' mb='0.5em'>
                      {endpoint.tableName === "places"
                        ? "Location"
                        : endpoint.tableName === "family-areas"
                          ? "Area"
                          : endpoint.tableName === "trips"
                            ? "Route"
                            : "Geometry"}
                    </Typography>
                    <Paper
                      variant='outlined'
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "1.5rem",
                        p: "0.62rem 0.88rem",
                        bgcolor: "#fff",
                        borderRadius: "0.5rem"
                      }}
                    >
                      <Stack
                        direction='row'
                        sx={{
                          gap: "0.5em",
                          alignItems: "center"
                        }}
                      >
                        <FmdGoodOutlined sx={{ fontSize: "1.3rem", color: selectedCoordinates ? "grey.600" : "grey.400" }} />
                        <GeometryInfo
                          endpoint={{ tableName: endpoint?.tableName }}
                          geometry={formData?.geometry}
                          selectedPolygon={selectedPolygon}
                          selectedCoordinates={selectedCoordinates || undefined}
                        />
                      </Stack>

                      {(selectedCoordinates || selectedPolygon || formData.geometry) && (
                        <IconButton onClick={handleClearCoordinates} size='small'>
                          <RefreshOutlined />
                        </IconButton>
                      )}
                    </Paper>
                  </Box>

                  {/* Advanced GeoJSON Editor */}
                  <Accordion
                    sx={{
                      boxShadow: "none",
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "transparent",
                      borderRadius: "0.5rem !important",
                      "::before": {
                        background: "transparent"
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        padding: "0.56rem 0.94rem",
                        minHeight: "3rem!important", // set fixed height for extended state
                        backgroundColor: "transparent",
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          margin: "0 !important" // override margin for extended state
                        }
                      }}
                    >
                      <Typography variant='body1' color='grey.500'>
                        Advanced GeoJSON Editor{" "}
                        <Typography component='span' color='grey.400'>
                          (For complex geometries, Features, FeatureCollections, track exports)
                        </Typography>
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TextField
                        name='geometry'
                        value={formData.geometry}
                        onChange={handleInputChange}
                        fullWidth
                        multiline
                        rows={8}
                        disabled={isSubmitting}
                        placeholder={`Examples:
Point: {
  "type": "Point",
  "coordinates": [longitude, latitude]
}

Feature: {
  "type": "Feature",
  "geometry": { "type": "Point", "coordinates": [lng, lat] },
  "properties": { "name": "My Place" }
}

FeatureCollection: {
  "type": "FeatureCollection",
  "features": [
    { "type": "Feature", "geometry": {...}, "properties": {...} }
  ]
}

LineString: {
  "type": "LineString",
  "coordinates": [[lng,lat], [lng,lat], ...]
}

Polygon: {
  "type": "Polygon",
  "coordinates": [[[lng,lat], [lng,lat], ...]]
}

MultiLineString: {
  "type": "MultiLineString",
  "coordinates": [[[lng,lat], [lng,lat]], [[lng,lat], [lng,lat]]]
}

MultiPolygon: {
  "type": "MultiPolygon",
  "coordinates": [[[[lng,lat], [lng,lat], ...]]]
}`}
                        sx={{
                          background: "#F3F3F5",
                          "& .MuiInputBase-input": {
                            fontFamily: "monospace",
                            fontSize: "0.875rem"
                          },
                          "& fieldset": {
                            borderColor: "transparent"
                          }
                        }}
                        className='scroller'
                        helperText={geoJsonValidationMessage}
                        error={hasInvalidGeoJson}
                      />
                    </AccordionDetails>
                  </Accordion>
                </>
              )}
            </Stack>
          </Grid>

          {/* Map */}
          {shouldShowMap && (
            <Grid item xs={5} sx={{ minWidth: 0, maxWidth: "50%", padding: "1.5rem", backgroundColor: "grey.50" }}>
              <Stack gap={"1.5rem"}>
                {geometryType === "Point" && (
                  <Typography variant='h5' component={"p"} color='grey.800' fontWeight='500'>
                    Drop a pin on the map
                  </Typography>
                )}

                <Box
                  width='100%'
                  position='relative'
                  height='100dvh'
                  sx={{
                    maxHeight: `calc(100dvh - ${geometryType === "Point" ? "21rem" : "18rem"})`,
                    borderRadius: "0.5rem",
                    overflow: "hidden",
                    "& .maplibregl-map": {
                      width: "100% !important",
                      maxWidth: "100% !important"
                    },
                    "& .maplibregl-canvas": {
                      maxWidth: "100% !important"
                    }
                  }}
                >
                  {/* CM tables (create + edit): inline draw map with geometry tools */}
                  {isCmTableEndpoint(endpoint) && (endpoint as any)?.spatialType && (endpoint as any).spatialType !== "none" ? (
                    <CmTableDrawMap
                      spatialType={(endpoint as any).spatialType ?? "polygon"}
                      allowedTypes={(endpoint as any).spatialType === "geometry" ? ["point", "linestring", "polygon"] : undefined}
                      initialViewState={initialViewState}
                      externalGeometry={parsedExternalGeometry}
                      previewColor={cmPreviewColor}
                      previewIcon={cmPreviewIcon}
                      onGeometryChange={(geometry) => {
                        const geoStr = geometry ? JSON.stringify(geometry, null, 2) : "";
                        setFormData((prev) => ({ ...prev, geometry: geoStr }));
                        if (geometry) handleGeometryChange(geoStr);
                      }}
                    />
                  ) : (
                    <PreviewMap
                      points={mapData.points}
                      areas={mapData.areas}
                      autoZoom={shouldAutoZoom}
                      mapStyle='satellite'
                      onClick={endpoint.tableName === "places" ? handleMapClick : undefined}
                      initialViewState={initialViewState}
                      showLabels={false}
                    />
                  )}

                  {/* Coordinate Search - only for places */}
                  {endpoint.tableName === "places" && (
                    <Box
                      position='absolute'
                      top={8}
                      left={8}
                      right={8}
                      zIndex={1000}
                      display='flex'
                      gap={1}
                      sx={{
                        maxWidth: "calc(100% - 16px)",
                        width: "auto"
                      }}
                    >
                      <TextField
                        value={mapSearchTerm}
                        onChange={(e) => setMapSearchTerm(e.target.value)}
                        placeholder='lat, lng'
                        size='small'
                        sx={{
                          backgroundColor: "#fff",
                          borderRadius: "0.5rem",
                          flex: 1,
                          minWidth: 0,
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white"
                          }
                        }}
                        onKeyUp={(e) => {
                          if (e.key === "Enter" && mapSearchTerm.trim()) {
                            handleCoordinateSearch(mapSearchTerm.trim());
                          }
                        }}
                      />
                      <Button
                        variant='contained'
                        onClick={() => {
                          if (mapSearchTerm.trim()) {
                            handleCoordinateSearch(mapSearchTerm.trim());
                          }
                        }}
                        disabled={!mapSearchTerm.trim()}
                        size='small'
                        sx={{ flexShrink: 0 }}
                      >
                        GO
                      </Button>
                    </Box>
                  )}
                </Box>

                {/* Actions Button*/}
                <FormActionButton
                  onDelete={() => setShowDeleteDialog(true)}
                  onSubmit={handleSubmit}
                  onCancel={handleCancel}
                  isSubmitting={isSubmitting}
                  isPending={isSubmitting}
                  hasPermissionToDelete={canDelete}
                  hasPermissionToSave={mode === "edit" ? canUpdate : canCreate}
                  mode={mode}
                  disabled={!formData?.name?.trim() || isLoading}
                />
              </Stack>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      {/* Footer Actions */}
      {
        // if map is not rendered show action buttons for others entity types
        !shouldShowMap && (
          <FormActionButton
            onDelete={() => setShowDeleteDialog(true)}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            isPending={isSubmitting}
            hasPermissionToDelete={canDelete}
            hasPermissionToSave={mode === "edit" ? canUpdate : canCreate}
            mode={mode}
            disabled={!formData?.name?.trim() || isLoading}
          />
        )
      }

      {/* RelationshipEditDialog for Edit Relationships */}
      {currentItem && endpoint && (
        <RelationshipEditDialog
          open={showEditRelationships}
          onClose={() => {
            setIsInRelationshipUpdate(false);
            setShowEditRelationships(false);
          }}
          item={
            {
              ...(currentItem as any),
              // Merge relatedMedia (local truth) into relationships.media so the diff
              // baseline doesn't omit media the user just uploaded/unlinked before this dialog opened.
              relationships: {
                byTableSlug: (currentItem as any)?.relationships?.byTableSlug ?? {},
                media: relatedMedia
                  .filter((m: any) => !m.file)
                  .map((m: any) => ({
                    id: m.id,
                    name: m.entity_name || m.name || "",
                    relationship_id: m.relationship_id,
                    entity_thumbnail: m.entity_thumbnail,
                    media_type: m.media_type || m.type
                  }))
              }
            } as any
          }
          endpoint={endpoint}
          initialEditorState={pendingRelEditorState ?? undefined}
          onApply={(editorState, payload) => {
            setPendingRelEditorState(editorState);
            pendingRelPayloadRef.current = payload;
            setIsInRelationshipUpdate(false);
          }}
          restrictionIds={selectedRestrictionIds}
          onRestrictionsChange={setSelectedRestrictionIds}
        />
      )}

      {showRestrictionsField && (
        <RestrictionsPickerDialog
          open={showRestrictionsPicker}
          value={selectedRestrictionIds}
          onClose={() => setShowRestrictionsPicker(false)}
          onSave={setSelectedRestrictionIds}
        />
      )}

      {/* Entity delete confirmation dialog */}
      {currentItem && (
        <DeleteDialog
          open={showDeleteDialog}
          onCancel={() => setShowDeleteDialog(false)}
          item={currentItem}
          onDelete={handleDelete}
          isPending={isEntityDeleting}
        />
      )}

      {/* Media Delete Confirmation Dialog */}
      {deleteMediaItem && (
        <DeleteDialog
          open={deleteDialogOpen}
          onCancel={() => setDeleteDialogOpen(false)}
          item={deleteMediaItem}
          onDelete={handleDeleteMediaItem}
          isPending={isMediaItemDeleting}
        />
      )}

      {/* Media Unlink Confirmation Dialog */}
      {dialogRelationships && (
        <MediaUnlinkDialog
          open={unlinkDialogOpen}
          onCancel={() => setUnlinkDialogOpen(false)}
          item={{ name: currentItem?.name }}
          relationships={dialogRelationships}
          onUnlink={handleUnlinkMedia}
          isPending={isMediaUnklinkPending}
        />
      )}

      {/* Upload media progress bar */}
      {uploadProgress && uploadMediaDetails && showUploadProgressModal && (
        <Dialog maxWidth='lg' open>
          <DialogContent>
            <UploadProgressBars
              progress={uploadProgress}
              items={uploadMediaDetails || []}
              onComplete={handleUploadAcknowledge}
              onFailure={handleUploadFailed}
              isFailed={isUploadFailed}
              isFinish={isUploadFinish}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Loading overlay to show data being fetching status */}
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            position: "absolute",
            alignItems: "center",
            height: "100%",
            width: "100%",
            inset: 0,
            bgcolor: "#ffffffcf",
            justifyContent: "center",
            backdropFilter: "blur(6px)",
            zIndex: 999
          }}
        >
          <Stack
            sx={{
              alignItems: "center",
              minWidth: "18rem",
              gap: "1rem",
              bgcolor: "common.white",
              padding: "1.5rem",
              borderRadius: "0.5rem",
              border: "1px solid",
              borderColor: "grey.200"
            }}
          >
            <CircularProgress size={"1.5rem"} />
            <Typography variant='body2' fontWeight={500} color='grey.700'>
              Preparing form to edit. Please wait a moment.
            </Typography>

            <Button variant='secondary-outlined' sx={{ mt: "1rem" }} onClick={handleCancel}>
              Cancel
            </Button>
          </Stack>
        </Box>
      )}
    </Dialog>
  );
}
